import AuthService from './authService';
import { readSummaryUpdateEvents, SummaryUpdateEvent, SummaryUpdateResult, SummaryUpdateError, SummaryUpdateInterruptedError, SummaryRequestNotStartedError } from './summaryUpdateStream';
import { SummaryUpdatePreferences } from '../utils/summaryUpdatePreferences';
import { FRONTEND_FEATURE_DEFAULTS } from '../utils/frontendFeatures';
import { Recording, Summary, MeetingType, ProcessingStatus, SupportingDocument, Tag, FileShare, FileSharePermission, CreateFileShareInput, TeamGroupUsersResponse, SharedPermissionLevel, UserPreferences, TemplatePreview, TemplateComponentMode, TemplateComponent, TemplateSuggestion, TemplateSuggestionStatus, SmartTemplateSchema, SmartTemplateField, ExtractedFieldItem, TemplateDraft, TemplateDraftStatus, AdditionalSettings, SectionLengthLimits, SummaryRef, RecordingSearchMatch, RecordingSearchResult, RecordingSearchResponse, RecordingSearchSort, ExportConfiguration, ExportPushResult, PraktijkdataSearchResponse, PraktijkdataSearchResult, ExportUserSettings, PraktijkdataMedewerker, EduVSearchResponse, EduVSearchResult, EduVEmployee, MijnKantoorSearchResponse, MijnKantoorSearchResult, AfasSearchResponse, AfasSearchResult, ZgwSearchResponse, ZgwSearchResult, ZgwInformatieobjecttype, MFilesSearchResponse, MFilesSearchResult } from '../types';
import { assertValidFileId, debugFileId } from '../utils/fileIdDebug';
import { logger } from '../utils/logger';
import { getSummaryRef } from '../utils/templateRef';
import { HubSpotSearchResult } from '../types';

export type SummaryStreamUpdate = {
    event?: SummaryUpdateEvent['event'];
    phase?: string;
    metadata?: Record<string, unknown>;
    result?: SummaryUpdateResult;
    title?: string;
    content?: string;
    isComplete: boolean;
    isTextReplacement?: boolean;
    pendingTarget?: string;
    releasedTarget?: string;
    statusMessage?: string;
};

// 404 op een template-endpoint: onbekende uuid, of een template dat de gebruiker
// niet (meer) ziet. De editor ververst de lijst en gaat terug naar het overzicht.
export class TemplateNotFoundError extends Error {
    constructor(message = 'Template niet gevonden') {
        super(message);
        this.name = 'TemplateNotFoundError';
    }
}

// 403 op een schrijfactie: geen beheerrechten op een organisatie- of groepstemplate.
export class TemplatePermissionError extends Error {
    constructor(message = 'Onvoldoende rechten om dit template te bewerken') {
        super(message);
        this.name = 'TemplatePermissionError';
    }
}

export interface SummaryGenerationOptions {
    summaryGenerationVersion?: 'v2' | 'v3';
    llmModel?: string;
    platform?: string;
    vertexRegion?: string;
    templateRevisionId?: number;
    signal?: AbortSignal;
}

const UPLOAD_REQUEST_TIMEOUT_MS = 5 * 60 * 1000;

export interface AudioStreamInfo {
    url: string;
    expiresAt: string | null;
    tokenTtlSeconds: number | null;
    mime: string | null;
    size: number | null;
    duration: number | null;
}

class ApiService {
    private authService: AuthService;

    constructor() {
        this.authService = AuthService.getInstance();
    }

    private get api() {
        return this.authService.getApi();
    }

    private mapApiStatusToProcessingStatus(status: string): ProcessingStatus {
        switch (status) {
            case 'Completed':
                return ProcessingStatus.PROCESSED;
            case 'Failed':
                return ProcessingStatus.NOT_PROCESSED;
            case 'Error':
                return ProcessingStatus.ERROR;
            case 'Queued':
                return ProcessingStatus.QUEUED;
            case 'Processing':
                return ProcessingStatus.PROCESSING;
            case 'Uploading':
                return ProcessingStatus.UPLOADING;
            case 'Unknown':
            default:
                return ProcessingStatus.NOT_STARTED;
        }
    }

    // Zolang de opname nog verwerkt wordt is de duur simpelweg nog niet bekend.
    // Dan geven we een lege string terug: de UI laat de duur dan weg in plaats
    // van een misleidende 0:00 te tonen.
    private formatDuration(durationInSeconds: number): string {
        if (!durationInSeconds || durationInSeconds <= 0) {
            return '';
        }

        const hours = Math.floor(durationInSeconds / 3600);
        const minutes = Math.floor((durationInSeconds % 3600) / 60);
        const seconds = Math.floor(durationInSeconds % 60);

        if (hours > 0) {
            return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        } else {
            return `${minutes}:${seconds.toString().padStart(2, '0')}`;
        }
    }

    private mapFileShare(apiShare: any): FileShare {
        if (!apiShare) {
            return {
                id: 0,
                targetType: 'user',
                permission: 'view'
            };
        }

        const parseId = (value: any): number => {
            if (value === null || value === undefined || value === '') {
                return 0;
            }
            const parsed = Number(value);
            return Number.isNaN(parsed) ? 0 : parsed;
        };

        const parseOptionalNumber = (value: any): number | undefined => {
            if (value === null || value === undefined || value === '') {
                return undefined;
            }
            const parsed = Number(value);
            return Number.isNaN(parsed) ? undefined : parsed;
        };

        const parseOptionalString = (value: any): string | undefined => {
            if (value === null || value === undefined) {
                return undefined;
            }
            const strValue = value.toString().trim();
            return strValue.length === 0 ? undefined : strValue;
        };

        const rawTargetType = apiShare.target_type || apiShare.targetType;
        const targetType = rawTargetType === 'group' ? 'group' : 'user';

        const rawPermission = (apiShare.permission || apiShare.permission_type || apiShare.access || 'view').toString().toLowerCase();
        const permission: FileSharePermission = ['view', 'edit', 'download', 'manage'].includes(rawPermission)
            ? rawPermission as FileSharePermission
            : 'view';

        const target = apiShare.target || {};
        const rawIsActive = typeof apiShare.is_active === 'boolean' ? apiShare.is_active : undefined;
        const revokedAt = apiShare.revoked_at ?? apiShare.revokedAt ?? null;
        const status = apiShare.status
            || (rawIsActive === false ? 'revoked' : undefined)
            || (revokedAt ? 'revoked' : undefined)
            || (rawIsActive === true ? 'active' : undefined)
            || 'active';
        const isActive = typeof rawIsActive === 'boolean' ? rawIsActive : status !== 'revoked';

        return {
            id: parseId(apiShare.id ?? apiShare.share_id),
            targetType,
            targetUserUuid: parseOptionalString(
                apiShare.target_user_uuid
                ?? apiShare.targetUserUuid
                ?? apiShare.user_uuid
                ?? apiShare.userUuid
                ?? target.user_uuid
                ?? target.userUuid
            ),
            targetGroupId: parseOptionalNumber(apiShare.target_group_id ?? apiShare.targetGroupId ?? target.group_id ?? target.groupId),
            targetId: parseOptionalNumber(target.id ?? target.user_id ?? target.userId),
            permission,
            expiresAt: apiShare.expires_at ?? apiShare.expiresAt ?? null,
            note: apiShare.note ?? apiShare.internal_note ?? null,
            status,
            isActive,
            createdAt: apiShare.created_at ?? apiShare.createdAt,
            updatedAt: apiShare.updated_at ?? apiShare.updatedAt,
            revokedAt,
            targetName: parseOptionalString(
                apiShare.target_name
                ?? apiShare.targetName
                ?? target.full_name
                ?? target.name
                ?? target.username
                ?? target.email
            ),
            targetEmail: parseOptionalString(apiShare.target_email ?? apiShare.targetEmail ?? target.email),
            targetIdentifier: parseOptionalString(
                apiShare.target_identifier
                ?? apiShare.targetIdentifier
                ?? target.username
                ?? target.email
            ),
            revokedBy: apiShare.revoked_by ?? apiShare.revokedBy ?? null,
        };
    }

    private normalizePermissionFlag(value: any): boolean | undefined {
        if (typeof value === 'boolean') {
            return value;
        }

        if (typeof value === 'number') {
            if (value === 1) return true;
            if (value === 0) return false;
        }

        if (typeof value === 'string') {
            const normalized = value.trim().toLowerCase();
            if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
            if (['false', '0', 'no', 'n'].includes(normalized)) return false;
        }

        return undefined;
    }

    private normalizeSharePermission(value: any): SharedPermissionLevel | null {
        if (value === null || value === undefined) {
            return null;
        }

        const normalized = value.toString().trim().toLowerCase();
        switch (normalized) {
            case 'view':
            case 'edit':
            case 'download':
            case 'manage':
                return normalized as SharedPermissionLevel;
            case 'owner':
                return 'owner';
            default:
                return null;
        }
    }

    private deriveAccessFromSharedPermission(permission: SharedPermissionLevel | null): {
        canEdit?: boolean;
        canDownload?: boolean;
        canManage?: boolean;
    } {
        if (!permission) {
            return {};
        }

        const isOwner = permission === 'owner';
        return {
            canEdit: permission === 'edit' || permission === 'download' || permission === 'manage' || isOwner,
            canDownload: permission === 'download' || permission === 'manage' || isOwner,
            canManage: permission === 'manage' || isOwner,
        };
    }

    // Nieuwe helper functie om XML response elementen te verwerken
    private processXmlResponse(
        buffer: string,
        currentContent: string
    ): { content: string; hasTextReplacement: boolean; updatedBuffer: string; releasedTarget?: string } {
        // Check voor complete <response> met text replacement tags
        const responseMatch = buffer.match(/<response>([\s\S]*?)<\/response>/);
        if (responseMatch) {
            const responseContent = responseMatch[1];

            // Extract de elementen - ondersteun zowel original_sentence als expanded_sentence
            const originalMatch = responseContent.match(/<original_sentence>([\s\S]*?)<\/original_sentence>/) ||
                responseContent.match(/<expanded_sentence>([\s\S]*?)<\/expanded_sentence>/);
            const revisedMatch = responseContent.match(/<revised_text>([\s\S]*?)<\/revised_text>/);

            if (originalMatch && revisedMatch) {
                const originalSentence = originalMatch[1].trim();
                const revisedText = revisedMatch[1].trim();

                //logger.log('Text replacement detected:', { originalSentence, revisedText });

                // Vervang de original_sentence met revised_text in de huidige content (alle instanties)
                if (currentContent.includes(originalSentence)) {
                    const escapedOriginal = originalSentence.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    const updatedContent = currentContent.replace(new RegExp(escapedOriginal, 'g'), revisedText);
                    //logger.log('Text replacement applied successfully');

                    // Verwijder de verwerkte response uit de buffer
                    const remainingBuffer = buffer.replace(responseMatch[0], '');
                    return { content: updatedContent, hasTextReplacement: true, updatedBuffer: remainingBuffer, releasedTarget: originalSentence };
                } else {
                    //logger.warn('Original sentence not found in current content for replacement');
                    //logger.log('Current content length:', currentContent.length);
                    //logger.log('Looking for:', originalSentence);

                    // Als we de originele tekst niet kunnen vinden, probeer een meer flexibele match
                    const escapedOriginal = originalSentence.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    const flexibleMatch = new RegExp(escapedOriginal, 'ig');

                    if (flexibleMatch.test(currentContent)) {
                        const updatedContent = currentContent.replace(flexibleMatch, revisedText);
                        //logger.log('Text replacement applied with flexible matching');

                        const remainingBuffer = buffer.replace(responseMatch[0], '');
                        return { content: updatedContent, hasTextReplacement: true, updatedBuffer: remainingBuffer, releasedTarget: originalSentence };
                    } else {
                        // Als laatste redmiddel, voeg de revised text toe
                        //logger.log('Adding revised text as new content');
                        const remainingBuffer = buffer.replace(responseMatch[0], '');
                        return { content: currentContent + '\n\n' + revisedText, hasTextReplacement: true, updatedBuffer: remainingBuffer, releasedTarget: originalSentence };
                    }
                }
            }
        }

        return { content: currentContent, hasTextReplacement: false, updatedBuffer: buffer };
    }

    private normalizeStreamStatusMessage(message: string): string {
        return message
            .replace(/\r/g, '\n')
            .replace(/^data:\s*/gmi, '')
            .replace(/\n{2,}/g, '\n')
            .split('\n')
            .map(line => line.trim())
            .filter(Boolean)
            .join(' ')
            .trim();
    }

    private extractStreamStatusMessages(buffer: string): { messages: string[]; updatedBuffer: string } {
        let updatedBuffer = buffer;
        const messages: string[] = [];

        const taggedMessagePattern = /<(status|message|log|feedback|thinking|step|progress|phase)>([\s\S]*?)<\/\1>/gi;
        updatedBuffer = updatedBuffer.replace(taggedMessagePattern, (_match, _tag, content) => {
            const normalized = this.normalizeStreamStatusMessage(content);
            if (normalized) {
                messages.push(normalized);
            }
            return '';
        });

        const firstStructuredTagIndex = updatedBuffer.search(/<(summary|response|title|original_sentence|expanded_sentence|revised_text)\b/i);
        if (firstStructuredTagIndex > 0) {
            const leadingText = updatedBuffer.slice(0, firstStructuredTagIndex);

            if (!leadingText.includes('<')) {
                leadingText
                    .replace(/\r/g, '\n')
                    .split('\n')
                    .map(line => this.normalizeStreamStatusMessage(line))
                    .filter(Boolean)
                    .forEach(message => messages.push(message));

                updatedBuffer = updatedBuffer.slice(firstStructuredTagIndex);
            }
        }

        return {
            messages: Array.from(new Set(messages)),
            updatedBuffer,
        };
    }

    private mapSmartTemplateField(field: any): SmartTemplateField {
        return {
            code: (field?.code || '').toString().trim(),
            label: (field?.label || field?.code || '').toString().trim(),
            description: field?.description ? field.description.toString() : undefined,
            required: this.normalizePermissionFlag(field?.required) ?? false,
            multiline: this.normalizePermissionFlag(field?.multiline) ?? true,
            exportKey: field?.export_key || field?.exportKey ? (field.export_key || field.exportKey).toString().trim() : undefined,
        };
    }

    private mapSmartTemplateSchema(schema: any): SmartTemplateSchema | null {
        if (!schema || typeof schema !== 'object') {
            return null;
        }

        const fields = Array.isArray(schema.fields)
            ? schema.fields.map((field: any) => this.mapSmartTemplateField(field)).filter((field: SmartTemplateField) => field.code)
            : [];

        return {
            version: Number.isFinite(Number(schema.version)) ? Number(schema.version) : 1,
            syntax: 'field_markers',
            fields,
        };
    }

    private mapAdditionalSettings(raw: any): AdditionalSettings | null {
        if (!raw) return null;
        const settings = typeof raw === 'string' ? JSON.parse(raw) : raw;
        // Canonieke sleutel is "lengte"; de overige sleutels zijn backend-compatibiliteitsaliassen
        const rawLength = settings.lengte ?? settings.aantal_woorden ?? settings.woorden
            ?? settings.doellengte_woorden ?? settings.word_target ?? settings.length;
        return {
            goal: settings.doel_van_de_samenvatting || settings.doelvande_samenvatting || settings.goal || '',
            perspective: settings.perspectief || settings.perspective || '',
            audience: settings.doelgroep || settings.audience || '',
            style: settings.schrijfstijl || settings.style || '',
            tense: settings.tijd || settings.tense || '',
            structure: settings.structuur || settings.structure || '',
            length: rawLength === undefined || rawLength === null ? '' : String(rawLength),
        };
    }

    private serializeAdditionalSettings(settings?: AdditionalSettings | null): any | undefined {
        if (!settings) return undefined;
        const length = (settings.length || '').trim();
        const hasValues = [settings.goal, settings.perspective, settings.audience, settings.style, settings.tense, length].some(v => v.trim());
        if (!hasValues) return undefined;
        const payload: any = {
            doel_van_de_samenvatting: settings.goal.trim(),
            perspectief: settings.perspective.trim(),
            doelgroep: settings.audience.trim(),
            schrijfstijl: settings.style.trim(),
            tijd: settings.tense.trim(),
        };
        // Automatisch = sleutel weglaten; een vast aantal woorden gaat als getal mee
        if (length) payload.lengte = /^\d+$/.test(length) ? Number(length) : length;
        return payload;
    }

    private serializeSmartTemplateSchema(schema?: SmartTemplateSchema | null): any | undefined {
        if (!schema) {
            return undefined;
        }

        const fields = Array.isArray(schema.fields)
            ? schema.fields
                .map((field) => ({
                    code: field.code.trim(),
                    label: field.label.trim(),
                    description: field.description?.trim() || '',
                    required: Boolean(field.required),
                    multiline: Boolean(field.multiline),
                    ...(field.exportKey?.trim() ? { export_key: field.exportKey.trim() } : {}),
                }))
                .filter((field) => field.code)
            : [];

        return {
            version: Number.isFinite(Number(schema.version)) ? Number(schema.version) : 1,
            syntax: 'field_markers',
            fields,
        };
    }

    private mapExtractedFieldItem(item: any): ExtractedFieldItem {
        const rawValue = item?.value ?? item?.content ?? item?.text ?? null;

        return {
            code: (item?.code || '').toString().trim(),
            label: (item?.label || item?.code || '').toString().trim(),
            value: Array.isArray(rawValue)
                ? rawValue.map((value) => value?.toString?.() ?? '').filter(Boolean)
                : rawValue === null || rawValue === undefined
                    ? null
                    : rawValue.toString(),
            description: item?.description ? item.description.toString() : undefined,
            required: this.normalizePermissionFlag(item?.required) ?? false,
            multiline: this.normalizePermissionFlag(item?.multiline) ?? true,
            exportKey: item?.export_key || item?.exportKey ? (item.export_key || item.exportKey).toString().trim() : undefined,
        };
    }

    private getFirstNonEmptyString(...values: any[]): string {
        for (const value of values) {
            if (value === null || value === undefined) {
                continue;
            }

            const normalized = value.toString().trim();
            if (normalized) {
                return normalized;
            }
        }

        return '';
    }

    // `fallback` is het verslag waarmee is opgevraagd: get-summary stuurt geen
    // meeting_type of template_uuid mee, en de naam mag nooit de uuid worden.
    private mapSummary(fileId: string, summary: any, fallback?: SummaryRef): Summary {
        const meetingType = this.getFirstNonEmptyString(summary?.meeting_type, summary?.meetingType, fallback?.meetingType ?? '');

        return {
            id: this.getFirstNonEmptyString(summary?.id) || `${fileId}-${meetingType || 'summary'}`,
            fileId: fileId.toString(),
            meetingType,
            templateUuid: summary?.template_uuid !== undefined ? summary.template_uuid : fallback?.templateUuid,
            content: summary?.content || '',
            sourceContent: summary?.source_content ?? summary?.sourceContent ?? summary?.content ?? '',
            title: summary?.title || 'Samenvatting',
            createdAt: summary?.created_at || summary?.createdAt || new Date().toISOString(),
            updatedAt: summary?.updated_at || summary?.updatedAt || summary?.created_at || new Date().toISOString(),
            additionalRecordings: Array.isArray(summary?.additional_recordings)
                ? summary.additional_recordings.map((recordingId: any) => recordingId.toString())
                : Array.isArray(summary?.additionalRecordings)
                    ? summary.additionalRecordings.map((recordingId: any) => recordingId.toString())
                    : [],
            templateRevisionId: summary?.template_revision_id ?? summary?.templateRevisionId,
            supportingDocuments: summary?.supporting_documents || summary?.supportingDocuments || [],
            extractedFields: summary?.extracted_fields ?? summary?.extractedFields ?? null,
            extractedFieldItems: Array.isArray(summary?.extracted_field_items)
                ? summary.extracted_field_items.map((item: any) => this.mapExtractedFieldItem(item))
                : Array.isArray(summary?.extractedFieldItems)
                    ? summary.extractedFieldItems.map((item: any) => this.mapExtractedFieldItem(item))
                    : [],
            extractionErrors: Array.isArray(summary?.extraction_errors)
                ? summary.extraction_errors.map((error: any) => error?.toString?.() ?? '').filter(Boolean)
                : Array.isArray(summary?.extractionErrors)
                    ? summary.extractionErrors.map((error: any) => error?.toString?.() ?? '').filter(Boolean)
                    : [],
            templateSchema: this.mapSmartTemplateSchema(summary?.template_schema ?? summary?.templateSchema ?? summary?.smart_template_schema ?? summary?.smartTemplateSchema),
        };
    }

    // Recordings
    async getRecordings(): Promise<Recording[]> {
        try {
            const response = await this.api.get('/list');
            const recordings = response.data || [];

            // Map API response to frontend types
            return recordings.map((recording: any) => {
                const recordingDateTime = recording.recording_datetime ?? recording.recordingDatetime;
                const sharedPermission = this.normalizeSharePermission(recording.shared_permission ?? recording.sharedPermission);
                const accessFromPermission = this.deriveAccessFromSharedPermission(sharedPermission);
                const canEdit = this.normalizePermissionFlag(recording.can_edit ?? recording.canEdit);
                const canDownload = this.normalizePermissionFlag(recording.can_download ?? recording.canDownload);
                const canManage = this.normalizePermissionFlag(recording.can_manage ?? recording.canManage);

                const effectiveCanEdit = canEdit ?? accessFromPermission.canEdit;
                const effectiveCanDownload = canDownload ?? accessFromPermission.canDownload;
                const effectiveCanManage = canManage ?? accessFromPermission.canManage;

                const normalizedRecording: Recording = {
                    id: recording.id.toString(),
                    name: recording.name,
                    status: this.mapApiStatusToProcessingStatus(recording.status),
                    duration: this.formatDuration(recording.audio_duration),
                    ...(recordingDateTime ? { recordingDateTime } : {}),
                    createdAt: recording.created_at,
                    updatedAt: recording.created_at, // Gebruik created_at als updatedAt niet beschikbaar is
                    tags: recording.tags || [],
                    sharedPermission: sharedPermission ?? null,
                };

                if (effectiveCanEdit !== undefined) {
                    normalizedRecording.canEdit = effectiveCanEdit;
                }
                if (effectiveCanDownload !== undefined) {
                    normalizedRecording.canDownload = effectiveCanDownload;
                }
                if (effectiveCanManage !== undefined) {
                    normalizedRecording.canManage = effectiveCanManage;
                }

                return normalizedRecording;
            });
        } catch (error) {
            logger.error('Error fetching recordings:', error);
            throw error;
        }
    }

    async getRecording(fileId: string): Promise<Recording | null> {
        try {
            // Haal status en basis info op
            const statusResponse = await this.api.get(`/status/${fileId}`);
            const statusData = statusResponse.data;
            const recordingDateTime = statusData?.recording_datetime ?? statusData?.recordingDatetime;

            if (!statusData) {
                return null;
            }

            // Map de data naar Recording format
            const recording: Recording = {
                id: fileId,
                name: statusData.name || `Recording ${fileId}`,
                status: this.mapApiStatusToProcessingStatus(statusData.status),
                duration: this.formatDuration(statusData.audio_duration || 0),
                ...(recordingDateTime ? { recordingDateTime } : {}),
                createdAt: statusData.created_at || new Date().toISOString(),
                updatedAt: statusData.updated_at || statusData.created_at || new Date().toISOString()
            };

            return recording;
        } catch (error) {
            logger.error('Error fetching recording:', error);
            return null;
        }
    }

    async getFile(fileId: string): Promise<Recording | null> {
        try {
            // Gebruik het nieuwe /file endpoint dat dezelfde structuur heeft als /list items
            const response = await this.api.get(`/file/${fileId}`);
            const fileData = response.data;

            if (!fileData) {
                return null;
            }

            // Map de data naar Recording format (zelfde mapping als in getRecordings)
            const sharedPermission = this.normalizeSharePermission(fileData.shared_permission ?? fileData.sharedPermission);
            const accessFromPermission = this.deriveAccessFromSharedPermission(sharedPermission);
            const canEdit = this.normalizePermissionFlag(fileData.can_edit ?? fileData.canEdit);
            const canDownload = this.normalizePermissionFlag(fileData.can_download ?? fileData.canDownload);
            const canManage = this.normalizePermissionFlag(fileData.can_manage ?? fileData.canManage);

            const effectiveCanEdit = canEdit ?? accessFromPermission.canEdit;
            const effectiveCanDownload = canDownload ?? accessFromPermission.canDownload;
            const effectiveCanManage = canManage ?? accessFromPermission.canManage;
            const recordingDateTime = fileData.recording_datetime ?? fileData.recordingDatetime;

            const recording: Recording = {
                id: fileData.id.toString(),
                name: fileData.name || `Recording ${fileData.id}`,
                status: this.mapApiStatusToProcessingStatus(fileData.status),
                duration: this.formatDuration(fileData.audio_duration || 0),
                ...(recordingDateTime ? { recordingDateTime } : {}),
                createdAt: fileData.created_at || new Date().toISOString(),
                updatedAt: fileData.created_at || new Date().toISOString(),
                tags: fileData.tags || [],
                sharedPermission: sharedPermission ?? null,
            };

            if (effectiveCanEdit !== undefined) {
                recording.canEdit = effectiveCanEdit;
            }
            if (effectiveCanDownload !== undefined) {
                recording.canDownload = effectiveCanDownload;
            }
            if (effectiveCanManage !== undefined) {
                recording.canManage = effectiveCanManage;
            }

            return recording;
        } catch (error) {
            logger.error('Error fetching file:', error);
            return null;
        }
    }

    async getRecordingStatus(fileId: string): Promise<{
        status: ProcessingStatus;
        audioDuration?: number;
        startTime?: number;
        estimatedProcessingTime?: number;
    }> {
        try {
            const response = await this.api.get(`/status/${fileId}`);
            const data = response.data;
            const status = data.status;

            const processingStatus = this.mapApiStatusToProcessingStatus(status);

            return {
                status: processingStatus,
                audioDuration: data.audio_duration,
                startTime: data.start_time,
                estimatedProcessingTime: data.estimated_processing_time
            };
        } catch (error) {
            logger.error('Error fetching recording status:', error);
            return { status: ProcessingStatus.NOT_PROCESSED };
        }
    }

    async processFile(fileId: string): Promise<boolean> {
        try {
            const response = await this.api.post('/process', {
                file_id: fileId
            });
            return response.status === 202;
        } catch (error) {
            logger.error('Error processing file:', error);
            return false;
        }
    }

    async renameRecording(fileId: string, newName: string): Promise<boolean> {
        try {
            const response = await this.api.post(`/rename/${fileId}`, {
                newFileName: newName
            });
            return response.status === 200;
        } catch (error) {
            logger.error('Error renaming recording:', error);
            return false;
        }
    }

    async deleteRecording(fileId: string): Promise<boolean> {
        try {
            const response = await this.api.delete(`/delete/${fileId}`);
            return response.status === 200;
        } catch (error) {
            logger.error('Error deleting recording:', error);
            throw error;
        }
    }

    async updateRecordingDatetime(fileId: string, datetime: string): Promise<boolean> {
        try {
            const response = await this.api.post(`/file-progress/${fileId}/recording-datetime`, {
                recording_datetime: datetime
            });
            return response.status === 200;
        } catch (error) {
            logger.error('Error updating recording datetime:', error);
            throw error;
        }
    }

    // Verslag-endpoints: eerst template_uuid, bij een 404 nog één keer de naam.
    // Een 404 op de uuid kan betekenen dat het template intussen is verwijderd;
    // het verslag staat onder de naam en is daarmee nog te vinden. Ook veilig
    // voor de export-POSTs: hun 404's vallen vóór elke bijwerking.
    private async withSummaryRef<T>(summary: SummaryRef, request: (ref: string) => Promise<T>): Promise<T> {
        const ref = getSummaryRef(summary);
        if (ref === summary.meetingType) return request(ref);
        try {
            return await request(ref);
        } catch (error) {
            if ((error as { response?: { status?: number } }).response?.status !== 404) throw error;
            return request(summary.meetingType);
        }
    }

    async deleteSummary(fileId: string, summary: SummaryRef): Promise<boolean> {
        try {
            const response = await this.withSummaryRef(summary, ref =>
                this.api.delete(`/summaries/${fileId}/${encodeURIComponent(ref)}`));
            return response.status === 200;
        } catch (error) {
            logger.error('Error deleting summary:', error);
            throw error;
        }
    }

    // Transcripts
    async getTranscript(fileId: string): Promise<string> {
        try {
            const response = await this.api.get(`/get_processed_file/${fileId}`);
            if (response.data.status === 'exists') {
                return response.data.content;
            }
            return '';
        } catch (error) {
            logger.error('Error fetching transcript:', error);
            return '';
        }
    }

    // Meeting Types
    async getMeetingTypes(): Promise<MeetingType[]> {
        try {
            const response = await this.api.get('/summarize-types');
            const templates = response.data.templates || [];

            // Map de API response naar het frontend MeetingType interface
            return templates.map((template: any) => ({
                id: template.id,
                uuid: template.uuid ?? undefined,
                name: template.name,
                title: template.title,
                description: template.description,
                type: template.type,
                is_default: template.is_default,
                is_org_wide: template.is_org_wide,
                content: template.content,
                smartTemplateSchema: this.mapSmartTemplateSchema(template.smart_template_schema ?? template.smartTemplateSchema),
            }));
        } catch (error) {
            logger.error('Error fetching meeting types:', error);
            return [];
        }
    }

    // Template-fouten uit het contract: 404 (niet gevonden of niet zichtbaar) en
    // 403 (geen beheerrechten). Overige fouten handelt de aanroeper zelf af.
    private toTemplateAccessError(error: any): TemplateNotFoundError | TemplatePermissionError | null {
        const status = error?.response?.status;
        const message = error?.response?.data?.error || error?.response?.data?.message || undefined;
        if (status === 404) return new TemplateNotFoundError(message);
        if (status === 403) return new TemplatePermissionError(message);
        return null;
    }

    /**
     * GET /template/{ref}. `templateRef` is de uuid (zie getTemplateRef) of, bij
     * een oude backend, de naam. Gooit TemplateNotFoundError bij een 404.
     */
    async getTemplate(templateRef: string): Promise<MeetingType | null> {
        try {
            const response = await this.api.get(`/template/${encodeURIComponent(templateRef)}`);
            const template = response.data;

            return {
                id: 0, // ID not provided by this endpoint
                // uuid en name horen bij het template dat de backend teruggeeft;
                // dat kan een ander zijn dan templateRef (persoonlijke kopie).
                uuid: template.uuid ?? undefined,
                // Oude backends sturen geen name mee; templateRef is dan de naam
                // omdat de lijst ook nog geen uuid had.
                name: template.name ?? templateRef,
                title: template.title,
                description: template.description,
                type: template.is_org_wide ? 'organization' : 'personal',
                is_default: false,
                is_org_wide: template.is_org_wide,
                content: template.content,
                smartTemplateSchema: this.mapSmartTemplateSchema(template.smart_template_schema ?? template.smartTemplateSchema),
                additionalSettings: this.mapAdditionalSettings(template.additional_settings ?? template.additionalSettings),
            };
        } catch (error) {
            logger.error('Error fetching template:', error);
            const accessError = this.toTemplateAccessError(error);
            if (accessError instanceof TemplateNotFoundError) throw accessError;
            return null;
        }
    }

    /**
     * PUT /template/{ref}. Geeft de uuid terug van het template dat is bijgewerkt;
     * die kan afwijken van templateRef (persoonlijke kopie van een standaard).
     */
    async updateTemplate(
        templateRef: string,
        contentOrData: string | { content: string; smartTemplateSchema?: SmartTemplateSchema | null }
    ): Promise<{ uuid?: string }> {
        try {
            const payload = typeof contentOrData === 'string'
                ? { content: contentOrData }
                : {
                    content: contentOrData.content,
                    ...(contentOrData.smartTemplateSchema
                        ? { smart_template_schema: this.serializeSmartTemplateSchema(contentOrData.smartTemplateSchema) }
                        : {}),
                };

            const response = await this.api.put(`/template/${encodeURIComponent(templateRef)}`, payload);
            return { uuid: response.data?.uuid ?? undefined };
        } catch (error) {
            logger.error('Error updating template:', error);
            throw this.toTemplateAccessError(error) ?? error;
        }
    }

    // Templates: genereren en bijwerken met feedback

    /**
     * Generate a quick preview of template content (not saved).
     * Uses component_mode: "preview" for faster generation.
     * Optionally accepts an example document for context.
     */
    async generateTemplatePreview(
        feedback: string,
        templateRequirements?: string,
        model: 'claude' | 'vertex-claude' | 'qwen' | 'gemini' | 'gemini-flash' = 'claude',
        exampleDocument?: File
    ): Promise<TemplatePreview> {
        try {
            if (!feedback || !feedback.trim()) {
                throw new Error('Feedback is vereist');
            }

            let response;

            if (exampleDocument) {
                // Use FormData for multipart upload
                const formData = new FormData();
                formData.append('feedback', feedback.trim());
                formData.append('model', model);
                formData.append('component_mode', 'preview');
                if (templateRequirements && templateRequirements.trim()) {
                    formData.append('template_requirements', templateRequirements.trim());
                }
                formData.append('example_document', exampleDocument);

                response = await this.api.post('/generate-template', formData, {
                    timeout: 120000,
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } else {
                const payload: any = {
                    feedback: feedback.trim(),
                    model,
                    component_mode: 'preview'
                };
                if (templateRequirements && templateRequirements.trim()) {
                    payload.template_requirements = templateRequirements.trim();
                }
                response = await this.api.post('/generate-template', payload, { timeout: 120000 });
            }

            if (response.data?.status === 'error') {
                throw new Error(response.data?.error || 'Onbekende fout bij genereren van preview');
            }

            // Preview response has template_data instead of template
            const templateData = response.data?.template_data;
            if (!templateData) {
                throw new Error('Geen preview data ontvangen');
            }

            return {
                content: templateData.content || '',
                title: templateData.title,
                description: templateData.description,
                additionalSettings: templateData.additional_settings,
                smartTemplateSchema: this.mapSmartTemplateSchema(templateData.smart_template_schema ?? templateData.smartTemplateSchema),
            };
        } catch (error: any) {
            logger.error('Error generating template preview:', error);
            if (error?.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
            throw error instanceof Error ? error : new Error('Fout bij het genereren van preview');
        }
    }

    /**
     * Generate a full template (saved to database).
     * Optionally pass previewData to finalize a previously generated preview.
     */
    async generateTemplate(
        feedback: string,
        templateRequirements?: string,
        model: 'claude' | 'vertex-claude' | 'qwen' | 'gemini' | 'gemini-flash' = 'claude',
        previewData?: TemplatePreview,
        summaryPreferences?: AdditionalSettings | null
    ): Promise<{
        id: number;
        title: string;
        name: string;
        description: string;
        complexity?: string;
    }> {
        try {
            if (!feedback || !feedback.trim()) {
                throw new Error('Feedback is vereist');
            }

            const payload: any = { feedback: feedback.trim(), model };
            if (templateRequirements && templateRequirements.trim()) {
                payload.template_requirements = templateRequirements.trim();
            }

            // If we have preview data, include it to help the backend finalize
            if (previewData) {
                payload.preview_content = previewData.content;
                if (previewData.title) payload.preview_title = previewData.title;
                if (previewData.description) payload.preview_description = previewData.description;
                if (previewData.smartTemplateSchema) {
                    payload.preview_smart_template_schema = this.serializeSmartTemplateSchema(previewData.smartTemplateSchema);
                }
            }

            // Expliciete voorkeuren (incl. lengte) gaan als summary_preferences mee,
            // zodat de backend ze deterministisch in additional_settings zet
            const serializedPreferences = this.serializeAdditionalSettings(summaryPreferences);
            if (serializedPreferences) {
                payload.summary_preferences = serializedPreferences;
            }

            const response = await this.api.post('/generate-template', payload, { timeout: 60000 });

            if (response.data?.status === 'error') {
                throw new Error(response.data?.error || 'Onbekende fout bij genereren van template');
            }

            const template = response.data?.template;
            return {
                id: template.id,
                title: template.title,
                name: template.name,
                description: template.description,
                complexity: template.complexity,
            };
        } catch (error: any) {
            logger.error('Error generating template:', error);
            if (error?.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
            throw error instanceof Error ? error : new Error('Fout bij het genereren van template');
        }
    }

    /**
     * Update a template with feedback.
     * Optionally specify which components to update via componentMode or targetComponents.
     */
    async updateTemplateWithFeedback(
        templateId: number,
        feedback: string,
        model: 'claude' | 'vertex-claude' | 'qwen' | 'gemini' | 'gemini-flash' = 'claude',
        options?: {
            componentMode?: TemplateComponentMode;
            targetComponents?: TemplateComponent[];
        }
    ): Promise<{ id: number; title: string; name: string; description: string; partial?: boolean; template_data?: TemplatePreview }> {
        try {
            if (!feedback || !feedback.trim()) {
                throw new Error('Feedback is vereist');
            }

            const payload: any = {
                feedback: feedback.trim(),
                model,
            };

            // Add component options if specified
            if (options?.componentMode) {
                payload.component_mode = options.componentMode;
            }
            if (options?.targetComponents && options.targetComponents.length > 0) {
                payload.target_components = options.targetComponents;
            }

            const response = await this.api.put(`/templates/${templateId}/update-with-feedback`, payload, { timeout: 60000 });

            if (response.data?.status === 'error') {
                throw new Error(response.data?.error || 'Onbekende fout bij bijwerken van template');
            }

            // Check if this is a partial response
            if (response.data?.partial) {
                return {
                    id: templateId,
                    title: response.data.template_data?.title || '',
                    name: '',
                    description: response.data.template_data?.description || '',
                    partial: true,
                    template_data: response.data.template_data ? {
                        content: response.data.template_data.content || '',
                        title: response.data.template_data.title,
                        description: response.data.template_data.description,
                        additionalSettings: response.data.template_data.additional_settings,
                        smartTemplateSchema: this.mapSmartTemplateSchema(response.data.template_data.smart_template_schema ?? response.data.template_data.smartTemplateSchema),
                    } : undefined,
                };
            }

            const template = response.data?.template || response.data;
            return {
                id: template.id,
                title: template.title,
                name: template.name,
                description: template.description,
            };
        } catch (error: any) {
            logger.error('Error updating template with feedback:', error);
            if (error?.response?.status === 403) {
                throw new Error('Geen toegang tot dit template');
            }
            if (error?.response?.status === 404) {
                throw new Error('Template niet gevonden');
            }
            if (error?.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
            throw error instanceof Error ? error : new Error('Fout bij het bijwerken van template');
        }
    }

    // DELETE /meeting-types/{ref}: alleen persoonlijke templates.
    async deleteTemplate(templateRef: string): Promise<boolean> {
        try {
            const response = await this.api.delete(`/meeting-types/${encodeURIComponent(templateRef)}`);
            return response.status === 200;
        } catch (error: any) {
            logger.error('Error deleting template:', error);
            if (error?.response?.status === 403) {
                throw new TemplatePermissionError('Geen toegang om dit template te verwijderen');
            }
            if (error?.response?.status === 404) {
                throw new TemplateNotFoundError();
            }
            throw error instanceof Error ? error : new Error('Fout bij het verwijderen van template');
        }
    }

    async updateTemplateMetadata(
        templateId: number,
        data: { title?: string; description?: string; revision_reason?: string }
    ): Promise<{ id: number; title: string; description: string }> {
        try {
            // Validatie: minstens title of description moet meegegeven worden
            if (!data.title && !data.description) {
                throw new Error('Minstens titel of beschrijving moet worden meegegeven');
            }

            // Validatie: title mag niet leeg zijn als meegegeven
            if (data.title !== undefined && data.title.trim() === '') {
                throw new Error('Titel mag niet leeg zijn');
            }

            const payload: { title?: string; description?: string; revision_reason?: string } = {};
            if (data.title) payload.title = data.title.trim();
            if (data.description !== undefined) payload.description = data.description.trim();
            if (data.revision_reason) payload.revision_reason = data.revision_reason.trim();

            const response = await this.api.post(`/templates/${templateId}/metadata`, payload);

            if (response.data?.template) {
                return {
                    id: response.data.template.id,
                    title: response.data.template.title,
                    description: response.data.template.description,
                };
            }

            throw new Error('Onverwacht response formaat');
        } catch (error: any) {
            logger.error('Error updating template metadata:', error);
            if (error?.response?.status === 400) {
                throw new Error(error.response.data?.error || 'Validatiefout bij bijwerken metadata');
            }
            if (error?.response?.status === 404) {
                throw new Error('Template niet gevonden of geen eigenaar');
            }
            if (error?.response?.status === 401) {
                throw new Error('Niet ingelogd');
            }
            throw error instanceof Error ? error : new Error('Fout bij het bijwerken van template metadata');
        }
    }

    // Template drafts (concept revisies)

    private mapSectionLengthLimits(raw: any): SectionLengthLimits | null {
        // Backend geeft null terug als er geen sectielimieten zijn herkend
        if (!raw || !Array.isArray(raw.limits) || raw.limits.length === 0) return null;
        return {
            limits: raw.limits.map((limit: any) => ({
                section: limit?.section || '',
                description: limit?.description || '',
                unit: limit?.unit || '',
                minValue: limit?.min_value ?? null,
                maxValue: limit?.max_value ?? null,
                maxWordsEquivalent: limit?.max_words_equivalent ?? null,
            })),
            sectionsTotal: Number(raw.sections_total ?? 0),
            sectionsWithLimit: Number(raw.sections_with_limit ?? 0),
            fullyAnnotated: Boolean(raw.fully_annotated),
            totalMaxWords: raw.total_max_words ?? null,
        };
    }

    private mapTemplateDraft(raw: any): TemplateDraft {
        const rawStatus = (raw?.status || 'draft').toString();
        const status: TemplateDraftStatus = rawStatus === 'applied'
            ? 'applied'
            : rawStatus === 'snapshot'
                ? 'snapshot'
                : 'draft';
        return {
            id: Number(raw?.id),
            templateId: raw?.template_id != null ? Number(raw.template_id) : undefined,
            templateUuid: raw?.template_uuid ?? undefined,
            revisionNumber: Number(raw?.revision_number ?? raw?.revisionNumber ?? 0),
            title: raw?.title || undefined,
            description: raw?.description || undefined,
            revisionReason: raw?.revision_reason ?? raw?.revisionReason ?? undefined,
            status,
            createdAt: raw?.created_at ?? raw?.createdAt ?? '',
            createdBy: raw?.created_by ?? raw?.createdBy ?? undefined,
            content: raw?.content ?? undefined,
            smartTemplateSchema: this.mapSmartTemplateSchema(raw?.smart_template_schema ?? raw?.smartTemplateSchema),
            additionalSettings: this.mapAdditionalSettings(raw?.additional_settings ?? raw?.additionalSettings),
            sectionLengthLimits: this.mapSectionLengthLimits(raw?.section_length_limits ?? raw?.sectionLengthLimits),
        };
    }

    // Alle /template/{ref}/drafts-methodes: templateRef is de uuid (zie getTemplateRef).
    async getTemplateDrafts(templateRef: string, options?: { limit?: number; includeApplied?: boolean }): Promise<TemplateDraft[]> {
        try {
            const params: Record<string, any> = {};
            if (options?.limit !== undefined) params.limit = options.limit;
            if (options?.includeApplied) params.include_applied = true;
            const response = await this.api.get(`/template/${encodeURIComponent(templateRef)}/drafts`, { params });
            const drafts = response.data?.drafts || [];
            return drafts.map((d: any) => this.mapTemplateDraft(d));
        } catch (error) {
            logger.error('Error fetching template drafts:', error);
            throw this.toTemplateAccessError(error) ?? error;
        }
    }

    async getTemplateDraft(templateRef: string, revisionId: number): Promise<TemplateDraft | null> {
        try {
            const response = await this.api.get(`/template/${encodeURIComponent(templateRef)}/drafts/${revisionId}`);
            return this.mapTemplateDraft(response.data);
        } catch (error) {
            logger.error('Error fetching template draft:', error);
            return null;
        }
    }

    async saveTemplateDraft(
        templateRef: string,
        data: {
            content: string;
            title?: string;
            description?: string;
            smartTemplateSchema?: SmartTemplateSchema | null;
            additionalSettings?: AdditionalSettings | null;
            revisionReason?: string;
        }
    ): Promise<TemplateDraft> {
        try {
            const payload: any = { content: data.content };
            if (data.title !== undefined) payload.title = data.title;
            if (data.description !== undefined) payload.description = data.description;
            if (data.smartTemplateSchema !== undefined) {
                payload.smart_template_schema = this.serializeSmartTemplateSchema(data.smartTemplateSchema);
            }
            const serializedSettings = this.serializeAdditionalSettings(data.additionalSettings);
            if (serializedSettings) payload.additional_settings = serializedSettings;
            if (data.revisionReason) payload.revision_reason = data.revisionReason;

            const response = await this.api.post(`/template/${encodeURIComponent(templateRef)}/drafts`, payload);
            const draft = response.data?.draft || response.data;
            // section_length_limits kan naast het draft-object in de respons staan
            if (draft && draft.section_length_limits === undefined && response.data?.section_length_limits !== undefined) {
                draft.section_length_limits = response.data.section_length_limits;
            }
            return this.mapTemplateDraft(draft);
        } catch (error: any) {
            logger.error('Error saving template draft:', error);
            const accessError = this.toTemplateAccessError(error);
            if (accessError) throw accessError;
            if (error?.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
            throw error instanceof Error ? error : new Error('Fout bij opslaan van concept');
        }
    }

    async deleteTemplateDraft(templateRef: string, revisionId: number): Promise<void> {
        try {
            await this.api.delete(`/template/${encodeURIComponent(templateRef)}/drafts/${revisionId}`);
        } catch (error: any) {
            logger.error('Error deleting template draft:', error);
            // Een 404 kan hier ook het concept zelf betreffen; alleen 403 is eenduidig
            const accessError = this.toTemplateAccessError(error);
            if (accessError instanceof TemplatePermissionError) throw accessError;
            if (error?.response?.status === 409) {
                throw new Error('Dit concept kan niet meer worden verwijderd (mogelijk al gepubliceerd of in gebruik)');
            }
            if (error?.response?.status === 404) {
                throw new Error('Concept niet gevonden');
            }
            if (error?.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
            throw error instanceof Error ? error : new Error('Fout bij verwijderen concept');
        }
    }

    // Geeft de uuid terug van het template waarop het concept is gepubliceerd.
    async applyTemplateDraft(templateRef: string, revisionId: number, reason?: string): Promise<{ templateUuid?: string }> {
        try {
            const response = await this.api.post(
                `/template/${encodeURIComponent(templateRef)}/drafts/${revisionId}/apply`,
                reason ? { reason } : {}
            );
            return { templateUuid: response.data?.template_uuid ?? undefined };
        } catch (error: any) {
            logger.error('Error applying template draft:', error);
            const accessError = this.toTemplateAccessError(error);
            if (accessError instanceof TemplatePermissionError) throw accessError;
            if (error?.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
            throw error instanceof Error ? error : new Error('Fout bij publiceren van concept');
        }
    }

    async generateFictiveMeeting(
        templateRef: string,
        revisionId: number,
        options: {
            templateTestPrompt: string;
            participants?: string[];
            durationMinutes?: number;
            language?: string;
            model?: string;
        }
    ): Promise<{ transcript: string; durationMinutes?: number; templateRevisionId?: number }> {
        try {
            const payload: any = {
                template_test_prompt: options.templateTestPrompt,
            };
            if (options.participants && options.participants.length > 0) payload.participants = options.participants;
            if (options.durationMinutes) payload.duration_minutes = options.durationMinutes;
            if (options.language) payload.language = options.language;
            if (options.model) payload.model = options.model;

            const response = await this.api.post(
                `/template/${encodeURIComponent(templateRef)}/drafts/${revisionId}/fictive-meeting`,
                payload,
                { timeout: 180000 }
            );
            const transcript = response.data?.transcript;
            if (typeof transcript !== 'string' || !transcript.trim()) {
                throw new Error('Geen transcript ontvangen');
            }
            return {
                transcript,
                durationMinutes: response.data?.duration_minutes ? Number(response.data.duration_minutes) : undefined,
                templateRevisionId: response.data?.template_revision_id ? Number(response.data.template_revision_id) : undefined,
            };
        } catch (error: any) {
            logger.error('Error generating fictive meeting:', error);
            if (error?.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
            throw error instanceof Error ? error : new Error('Fout bij genereren fictieve vergadering');
        }
    }

    // POST /template/{ref}/drafts/{id}/test-summary neemt de uuid, anders dan
    // /summarize-stream met template_revision_id (dat blijft op name).
    async *streamDraftTestSummary(
        templateRef: string,
        revisionId: number,
        options: { fileId?: string; testPrompt?: string; testTranscript?: string; additionalPrompt?: string; model?: string }
    ): AsyncGenerator<SummaryStreamUpdate, void, unknown> {
        try {
            const token = await this.authService.getValidAccessToken();
            const baseUrl = this.api.defaults.baseURL || '';
            const seenStatusMessages = new Set<string>();

            const requestBody: any = {
                model: options.model || 'auto',
            };
            if (options.fileId) {
                requestBody.file_id = Number(options.fileId);
            }
            if (options.testPrompt) {
                requestBody.template_test_prompt = options.testPrompt;
            }
            if (options.testTranscript) {
                requestBody.template_test_transcript = options.testTranscript;
            }
            if (options.additionalPrompt) {
                requestBody.additional_prompt = options.additionalPrompt;
            }

            yield { isComplete: false, statusMessage: 'Test gestart...' };

            const response = await fetch(`${baseUrl}/template/${encodeURIComponent(templateRef)}/drafts/${revisionId}/test-summary`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'Cache-Control': 'no-cache',
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                const errorText = await response.text();
                logger.error('Draft test stream failed:', { status: response.status, body: errorText });
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            yield { isComplete: false, statusMessage: 'Stream geopend, wacht op terugkoppeling van Notizy.' };

            const reader = response.body?.getReader();
            const decoder = new TextDecoder();
            if (!reader) throw new Error('No response body');

            let buffer = '';
            let currentTitle = '';
            let currentContent = '';
            let lastYieldedLength = 0;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });

                const statusExtraction = this.extractStreamStatusMessages(buffer);
                buffer = statusExtraction.updatedBuffer;
                for (const message of statusExtraction.messages) {
                    if (seenStatusMessages.has(message)) continue;
                    seenStatusMessages.add(message);
                    yield { isComplete: false, statusMessage: message };
                }

                const summaryMatch = buffer.match(/<summary>([\s\S]*?)(?:<\/summary>|$)/);
                if (summaryMatch) {
                    const extractedContent = summaryMatch[1];
                    if (extractedContent.length > lastYieldedLength) {
                        currentContent = extractedContent;
                        lastYieldedLength = extractedContent.length;
                        yield {
                            title: currentTitle,
                            content: currentContent,
                            isComplete: false,
                            statusMessage: 'Concept ontvangen, inhoud wordt bijgewerkt.',
                        };
                    }
                }

                const titleMatch = buffer.match(/<title>([\s\S]*?)(?:<\/title>|$)/);
                if (titleMatch && titleMatch[1] !== currentTitle) {
                    currentTitle = titleMatch[1];
                }

                if (buffer.includes('</response>')) break;
            }

            yield {
                title: currentTitle,
                content: currentContent,
                isComplete: true,
                statusMessage: 'Test afgerond.',
            };
        } catch (error) {
            logger.error('Error streaming draft test summary:', error);
            throw error;
        }
    }

    // Summaries
    async getSummaries(fileId: string): Promise<Summary[]> {
        try {
            const response = await this.api.get(`/summaries/${fileId}`);
            const summaries = response.data.summaries || [];

            return summaries.map((summary: any) => this.mapSummary(fileId, summary));
        } catch (error) {
            logger.error('Error fetching summaries:', error);
            return [];
        }
    }

    async getSummary(fileId: string, summary: SummaryRef): Promise<Summary | null> {
        try {
            const response = await this.withSummaryRef(summary, ref =>
                this.api.get(`/get-summary/${encodeURIComponent(fileId)}/${encodeURIComponent(ref)}`));
            return this.mapSummary(fileId, response.data, summary);
        } catch (error) {
            logger.error('Error fetching summary:', error);
            return null;
        }
    }

    async getStructuredSummary(fileId: string, summary: SummaryRef): Promise<Summary | null> {
        try {
            const response = await this.withSummaryRef(summary, ref =>
                this.api.get(`/api/structured-summaries/${fileId}/${encodeURIComponent(ref)}`));
            return this.mapSummary(fileId, response.data, summary);
        } catch (error) {
            logger.error('Error fetching structured summary:', error);
            return null;
        }
    }

    async createSummary(fileId: string, summary: SummaryRef): Promise<string> {
        try {
            const response = await this.api.post('/summarize', {
                file_id: fileId,
                meeting_type: getSummaryRef(summary)
            }, {
                timeout: 60000 // 1 minuut timeout voor samenvattingen
            });
            return response.data.content || '';
        } catch (error) {
            logger.error('Error creating summary:', error);
            throw error;
        }
    }

    async updateSummary(fileId: string, summary: SummaryRef, content: string): Promise<boolean> {
        const meetingType = getSummaryRef(summary);
        // Guard: don't send update with missing required parameters
        if (!fileId || !meetingType || !content) {
            logger.warn('updateSummary: skipping request due to missing required parameters', { fileId, meetingType: !!meetingType, content: !!content });
            return false;
        }

        try {
            const response = await this.withSummaryRef(summary, ref => this.api.post('/update-summary-raw', {
                file_id: fileId,
                meeting_type: ref,
                summary: content
            }));
            return response.status === 200;
        } catch (error) {
            logger.error('Error updating summary:', error);
            return false;
        }
    }

    async updateSummaryWithPrompt(fileId: string, summary: SummaryRef, prompt: string, selectedText?: string): Promise<string> {
        try {
            const response = await this.api.post('/update-summary', {
                file_id: fileId,
                meeting_type: getSummaryRef(summary),
                prompt,
                selected_text: selectedText
            }, {
                timeout: 60000
            });
            return response.data.content || '';
        } catch (error) {
            logger.error('Error updating summary with prompt:', error);
            throw error;
        }
    }

    // Streaming update voor nieuwe samenvattingen
    // Nieuw verslag: meeting_type is de uuid van het gekozen template (de backend
    // zoekt de naam erbij), ook bij een concepttest met template_revision_id.
    async *streamSummary(
        fileId: string,
        summary: SummaryRef,
        additionalRecordings: string[] = [],
        options: SummaryGenerationOptions = {},
    ): AsyncGenerator<SummaryStreamUpdate, void, unknown> {
        const body = {
            file_id: fileId,
            meeting_type: getSummaryRef(summary),
            model: 'claude',
            ...(options.platform ? { platform: options.platform } : {}),
            ...(options.llmModel ? { llm_model: options.llmModel } : {}),
            ...(options.vertexRegion ? { vertex_region: options.vertexRegion } : {}),
            ...(options.templateRevisionId !== undefined ? { template_revision_id: options.templateRevisionId } : {}),
            additional_recordings: additionalRecordings,
        };
        const useV3 = options.summaryGenerationVersion !== undefined
            ? options.summaryGenerationVersion === 'v3'
            : FRONTEND_FEATURE_DEFAULTS.summary_generation_v3;
        if (useV3) {
            yield* this.streamSummaryV3({ ...body, action: 'generate', dry_run: false }, options.signal);
        } else {
            yield* this.streamSummaryV2(body, summary, options.signal);
        }
    }

    private async requestSummaryStream(endpoint: string, body: Record<string, unknown>, signal?: AbortSignal): Promise<Response> {
        const token = await this.authService.getValidAccessToken().catch(error => {
            throw new SummaryRequestNotStartedError(error instanceof Error ? error.message : 'Aanmelden is vereist.');
        });
        const baseUrl = (this.api.defaults.baseURL || '').replace(/\/$/, '');
        if (!baseUrl.startsWith('https://')) throw new SummaryRequestNotStartedError('Verslagen vereisen een HTTPS API-server.');
        if (!token || signal?.aborted) throw new SummaryRequestNotStartedError('De verslagaanvraag is niet gestart.');
        // Obtain authentication before the single mutation. Never retry a possibly sent POST.
        return fetch(`${baseUrl}${endpoint}`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', Accept: 'text/event-stream' },
            body: JSON.stringify(body),
            signal,
        }).catch(() => { throw new SummaryUpdateInterruptedError(); });
    }

    private async *streamSummaryV2(body: { file_id: string; meeting_type: string }, summary: SummaryRef, signal?: AbortSignal): AsyncGenerator<SummaryStreamUpdate> {
        const response = await this.requestSummaryStream('/summarize-stream-v2', body, signal);
        if (!response.ok) throw new SummaryUpdateError(`HTTP ${response.status}: ${response.statusText}`, 'http_error');
        if (!response.body || response.headers.get('Content-Type')?.split(';')[0].trim().toLowerCase() !== 'text/event-stream') {
            void response.body?.cancel().catch(() => undefined);
            throw new SummaryUpdateInterruptedError();
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8', { fatal: true });
        let buffer = '';
        let content = '';
        let title: string | undefined;
        // Strip only protocol wrappers, preserving Markdown and structured field markers.
        const unwrap = (text: string) => text.replace(/^<!\[CDATA\[/, '').replace(/\]\]>$/, '');
        try {
            while (true) {
                const { done, value } = await reader.read();
                buffer += done ? decoder.decode() : decoder.decode(value, { stream: true });
                const error = buffer.match(/<error>([\s\S]*?)<\/error>/);
                if (error) {
                    const message = unwrap(error[1].match(/<message>([\s\S]*?)<\/message>/)?.[1] ?? 'De verslaggeneratie is mislukt.');
                    const code = error[1].match(/<type>([\s\S]*?)<\/type>/)?.[1] ?? 'stream_error';
                    // Only the new atomic queue-expiration signal proves the
                    // worker cannot start later. Legacy timeouts remain unknown.
                    if (code === 'patch_queue_expired' && !buffer.includes('<summary>')) {
                        throw new SummaryRequestNotStartedError(message);
                    }
                    throw new SummaryUpdateError(message, code);
                }
                const titleMatch = buffer.match(/<title>([\s\S]*?)<\/title>/);
                if (titleMatch) title = unwrap(titleMatch[1]);
                const summaryMatch = buffer.match(/<summary>([\s\S]*?)(?:<\/summary>|$)/);
                if (summaryMatch) {
                    // A chunk may stop halfway through a closing tag or CDATA marker.
                    let next = summaryMatch[1];
                    if (!buffer.includes('</summary>')) {
                        const suffixes = ['</summary>', '<![CDATA[', ']]>'];
                        for (const marker of suffixes) {
                            for (let length = marker.length - 1; length > 0; length--) {
                                if (next.endsWith(marker.slice(0, length))) { next = next.slice(0, -length); break; }
                            }
                        }
                    }
                    next = unwrap(next);
                    if (next !== content) {
                        content = next;
                        yield { event: 'preview', content, isComplete: false };
                    }
                }
                // Drain to EOF: the server can report a save failure after </response>.
                if (done) break;
            }
            if (!buffer.includes('</summary>') || !buffer.includes('</response>') || !content.trim()) {
                throw new SummaryUpdateInterruptedError();
            }
            // V2 has no saved result event. Confirm persistence before the editor
            // releases its generation lock or uploads any local edits.
            if (await this.getSummaryStatus(body.file_id, summary) !== 'completed') {
                throw new SummaryUpdateInterruptedError();
            }
            yield { event: 'result', content, title, isComplete: true,
                result: { mode: 'generate', summary: content, saved: true, ...(title !== undefined ? { title } : {}) } };
        } catch (error) {
            if (error instanceof SummaryUpdateError || error instanceof SummaryUpdateInterruptedError || error instanceof SummaryRequestNotStartedError) throw error;
            throw new SummaryUpdateInterruptedError();
        } finally {
            void reader.cancel().catch(() => undefined);
            reader.releaseLock();
        }
    }

    private async *streamSummaryV3(body: Record<string, unknown>, signal?: AbortSignal): AsyncGenerator<SummaryStreamUpdate> {
        const response = await this.requestSummaryStream('/summarize-stream-v3', body, signal);
        for await (const update of readSummaryUpdateEvents(response)) {
            if (body.action === 'generate' && update.event === 'result' && (update.data.mode !== 'generate' || !update.data.saved)) {
                throw new SummaryUpdateInterruptedError();
            }
            yield {
                event: update.event,
                metadata: update.data,
                phase: update.event === 'status' ? update.data.phase : undefined,
                content: update.event === 'preview' || update.event === 'result' ? update.data.summary : undefined,
                title: update.event === 'result' ? update.data.title : undefined,
                isComplete: update.event === 'result',
                result: update.event === 'result' ? update.data : undefined,
            };
        }
    }

    async getSummaryStatus(fileId: string, summary: SummaryRef): Promise<'in_progress' | 'completed' | 'failed' | 'not_found'> {
        try {
            const response = await this.withSummaryRef(summary, ref =>
                this.api.get(`/summary-status/${encodeURIComponent(fileId)}/${encodeURIComponent(ref)}`));
            const status = response.data?.status;
            if (['in_progress', 'completed', 'failed', 'not_found'].includes(status)) return status;
            throw new Error('Unknown summary status');
        } catch (error) {
            if ((error as { response?: { status?: number } }).response?.status === 404) return 'not_found';
            throw error;
        }
    }

    // Streaming update met prompts voor bestaande samenvattingen
    async *streamSummaryWithPrompt(
        fileId: string,
        summary: SummaryRef,
        prompt: string,
        selectedText?: string,
        resetSummary: boolean = false,
        currentSummaryContent?: string,
        additionalRecordings?: string[],
        options?: SummaryUpdatePreferences
    ): AsyncGenerator<SummaryStreamUpdate, void, unknown> {
        // Choose the protocol explicitly: both routes advertise text/event-stream.
        const isReset = resetSummary || prompt === 'Maak het verslag opnieuw';
        if (!isReset && options?.summaryUpdateVersion === 'v3') {
            yield* this.streamSummaryV3({
                file_id: fileId,
                meeting_type: getSummaryRef(summary),
                additional_prompt: selectedText?.trim()
                    ? `${prompt}\n\nPas deze opdracht toe op de geselecteerde tekst:\n${selectedText}`
                    : prompt,
                model: 'claude',
                platform: 'default',
                llm_model: options.llmModel,
                dry_run: false,
                ...(additionalRecordings?.length ? { additional_recordings: additionalRecordings } : {}),
            });
            return;
        }
        try {
            const token = await this.authService.getValidAccessToken();
            const baseUrl = this.api.defaults.baseURL || '';
            const seenStatusMessages = new Set<string>();

            const requestBody: any = {
                file_id: fileId,
                meeting_type: getSummaryRef(summary),
                model: 'claude',
                ...(!isReset && options ? { platform: 'default', llm_model: options.llmModel } : {}),
            };

            if (resetSummary || prompt === 'Maak het verslag opnieuw') {
                requestBody.reset_summary = true;
            }

            if (prompt && prompt !== 'Maak het verslag opnieuw') {
                requestBody.additional_prompt = prompt;
            }

            if (selectedText && selectedText.trim()) {
                requestBody.specific_sentence = selectedText;
            }

            if (additionalRecordings && additionalRecordings.length > 0) {
                requestBody.additional_recordings = additionalRecordings;
            }

            logger.log('Starting stream request with body:', requestBody);

            yield {
                isComplete: false,
                statusMessage: resetSummary || prompt === 'Maak het verslag opnieuw'
                    ? 'Opnieuw genereren gestart.'
                    : 'Feedback ontvangen en verwerking gestart.'
            };

            const endpoint = requestBody.additional_prompt ? '/summarize-stream-v2' : '/summarize-stream';
            const response = await fetch(`${baseUrl}${endpoint}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'text/event-stream',
                },
                body: JSON.stringify(requestBody)
            });

            logger.log('Stream response status:', response.status);
            logger.log('Stream response headers:', Object.fromEntries(response.headers.entries()));

            if (!response.ok) {
                const errorText = await response.text();
                logger.error('Stream request failed:', { status: response.status, statusText: response.statusText, body: errorText });
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            yield {
                isComplete: false,
                statusMessage: 'Stream geopend, wacht op terugkoppeling van Notizy.'
            };

            const reader = response.body?.getReader();
            const decoder = new TextDecoder();

            if (!reader) {
                throw new Error('No response body');
            }

            logger.log('Starting to read stream...');

            let buffer = '';
            let currentTitle = '';
            let currentContent = currentSummaryContent || ''; // Start met huidige content voor text replacements
            let lastYieldedLength = 0;

            try {
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    const chunk = decoder.decode(value, { stream: true });
                    // logger.log('Raw chunk received:', chunk);
                    buffer += chunk;

                    const statusExtraction = this.extractStreamStatusMessages(buffer);
                    buffer = statusExtraction.updatedBuffer;
                    for (const message of statusExtraction.messages) {
                        if (seenStatusMessages.has(message)) {
                            continue;
                        }
                        seenStatusMessages.add(message);
                        yield {
                            isComplete: false,
                            statusMessage: message
                        };
                    }

                    while (true) {
                        const xmlResult = this.processXmlResponse(buffer, currentContent);
                        if (!xmlResult.hasTextReplacement) break;
                        currentContent = xmlResult.content;
                        buffer = xmlResult.updatedBuffer;
                        yield { title: currentTitle, content: currentContent, isComplete: false,
                            isTextReplacement: true, releasedTarget: xmlResult.releasedTarget,
                            statusMessage: 'Gerichte tekstwijziging toegepast.' };
                    }
                    // A complete original tag identifies the passage while its replacement is still arriving.
                    const pendingTarget = buffer.match(/<(?:original_sentence|expanded_sentence)>([\s\S]*?)<\/(?:original_sentence|expanded_sentence)>/)?.[1].trim();
                    if (pendingTarget) yield { isComplete: false, pendingTarget };

                    // Simple approach: extract any text content and show incrementally
                    // Look for content between <summary> tags
                    const summaryMatch = buffer.match(/<summary>([\s\S]*?)(?:<\/summary>|$)/);
                    if (summaryMatch) {
                        const extractedContent = summaryMatch[1];

                        // Only update if we have new content
                        if (extractedContent.length > lastYieldedLength) {
                            currentContent = extractedContent;
                            lastYieldedLength = extractedContent.length;

                            //logger.log('Yielding incremental update with content length:', currentContent.length);

                            yield {
                                title: currentTitle,
                                content: currentContent,
                                isComplete: false,
                                statusMessage: 'Concept ontvangen, inhoud wordt bijgewerkt.'
                            };
                        }
                    }

                    // Look for title content
                    const titleMatch = buffer.match(/<title>([\s\S]*?)(?:<\/title>|$)/);
                    if (titleMatch && titleMatch[1] !== currentTitle) {
                        currentTitle = titleMatch[1];
                        logger.log('Updated title:', currentTitle);
                    }

                    // Check if we have a complete response
                    if (buffer.includes('</response>')) {
                        logger.log('Stream completed successfully');
                        break;
                    }
                }

                // Final update to mark completion
                yield {
                    title: currentTitle,
                    content: currentContent,
                    isComplete: true,
                    statusMessage: 'Verwerking afgerond.'
                };
            } finally {
                void reader.cancel().catch(() => undefined);
                reader.releaseLock();
            }

        } catch (error) {
            logger.error('Error streaming summary updates:', error);
            throw error;
        }
    }

    // Audio
    async getAudio(fileId: string): Promise<Blob> {
        try {
            const response = await this.api.get(`/audio/${fileId}`, {
                responseType: 'blob',
                timeout: 180000
            });
            return response.data;
        } catch (error) {
            logger.error('Error fetching audio:', error);
            throw error;
        }
    }

    // Vraagt een kortlevende stream-URL aan; de browser streamt daarmee zelf via Range-requests.
    // Eerste aanroep voor een opname kan server-side een synchrone MP3-conversie triggeren.
    async getAudioStreamUrl(fileId: string): Promise<AudioStreamInfo> {
        try {
            const response = await this.api.post(`/audio-stream-url/${fileId}`, null, {
                timeout: 180000
            });
            const data = response.data ?? {};
            return {
                url: new URL(data.url, this.authService.getDomain()).toString(),
                expiresAt: data.expires_at ?? null,
                tokenTtlSeconds: data.token_ttl_seconds ?? null,
                mime: data.mime ?? null,
                size: data.size ?? null,
                duration: data.duration ?? null
            };
        } catch (error) {
            logger.error('Error fetching audio stream URL:', error);
            throw error;
        }
    }

    // Document Downloads
    async downloadPdf(fileId: string, summary: SummaryRef): Promise<Blob> {
        try {
            const response = await this.withSummaryRef(summary, ref =>
                this.api.get(`/download-pdf/${fileId}/${encodeURIComponent(ref)}`, { responseType: 'blob' }));
            return response.data;
        } catch (error) {
            logger.error('Error downloading PDF:', error);
            throw error;
        }
    }

    async downloadWord(fileId: string, summary: SummaryRef): Promise<Blob> {
        try {
            const response = await this.withSummaryRef(summary, ref =>
                this.api.get(`/download-docx/${fileId}/${encodeURIComponent(ref)}`, { responseType: 'blob' }));
            return response.data;
        } catch (error) {
            logger.error('Error downloading Word document:', error);
            throw error;
        }
    }

    async downloadTranscript(fileId: string): Promise<Blob> {
        try {
            const response = await this.api.get(`/download-transcript/${fileId}`, {
                responseType: 'blob'
            });
            return response.data;
        } catch (error) {
            logger.error('Error downloading transcript:', error);
            throw error;
        }
    }

    // Externe exports (HubSpot/Notion/Drive/custom webhook)
    async getExportConfigurations(fileId: string, summary: SummaryRef): Promise<ExportConfiguration[]> {
        try {
            const response = await this.withSummaryRef(summary, ref =>
                this.api.get(`/export/export_configurations/${encodeURIComponent(fileId)}/${encodeURIComponent(ref)}`));
            const raw = response.data?.configurations || response.data || [];
            if (!Array.isArray(raw)) {
                return [];
            }
            return raw
                .filter((item: any) => item && (item.id ?? item.config_id) !== undefined)
                .map((item: any) => {
                    // Edu-V en M-Files hebben geen los endpoint voor
                    // export_method; hun flow leunt op deze waarde uit de lijst.
                    const method = (
                        item.configuration_details?.export_method ??
                        item.export_method ??
                        ''
                    ).toString();
                    const knownMethods = ['PROGRESS', 'LAS_WEBHOOK', 'DOCUMENT_LINKED', 'DOCUMENT'];
                    return {
                        id: (item.id ?? item.config_id).toString(),
                        targetPlatform: (item.target_platform ?? item.targetPlatform ?? '').toString(),
                        name: (item.name ?? item.label ?? '').toString(),
                        exportMethod: knownMethods.includes(method)
                            ? (method as ExportConfiguration['exportMethod'])
                            : undefined,
                        hubspot: (item.target_platform ?? item.targetPlatform) === 'HUBSPOT'
                            ? {
                                targetObjectType: (item.configuration_details?.target_object_type ?? 'UNKNOWN').toString(),
                                createIfNotFound: item.configuration_details?.create_if_not_found === true,
                            }
                            : undefined,
                    };
                });
        } catch (error) {
            logger.error('Error fetching export configurations:', error);
            throw error;
        }
    }

    async searchHubSpot(
        fileId: string,
        summary: SummaryRef,
        configId: string,
        searchTerm: string,
        searchProperty?: string
    ): Promise<HubSpotSearchResult[]> {
        const response = await this.withSummaryRef(summary, ref => this.api.post(
            `/export/${encodeURIComponent(fileId)}/${encodeURIComponent(ref)}/hubspot/search`,
            {
                config_id: configId,
                search_term: searchTerm,
                ...(searchProperty ? { search_property: searchProperty } : {}),
            },
            { timeout: 35000 }
        ));
        const results = response.data?.results;
        if (!Array.isArray(results)) return [];
        return results
            .filter((item: any) => item && item.id !== undefined && item.id !== null)
            .map((item: any) => ({
                id: item.id.toString(),
                name: (item.name ?? '').toString(),
            }));
    }

    // Net als in de app is HTTP 200/201 een succes, ook zonder success-veld.
    // De andere exportplatformen vereisen juist een expliciete success: true.
    private mapHubSpotExportResponse(data: any): ExportPushResult {
        const success = data?.success !== false && !data?.error;
        return {
            success,
            message: (success
                ? data?.display?.message ?? data?.message ?? ''
                : data?.error ?? data?.message ?? '').toString(),
        };
    }

    async linkHubSpot(
        fileId: string,
        summary: SummaryRef,
        configId: string,
        platformObjectId: string
    ): Promise<ExportPushResult> {
        const response = await this.withSummaryRef(summary, ref => this.api.post(
            `/export/${encodeURIComponent(fileId)}/${encodeURIComponent(ref)}/hubspot/link`,
            { config_id: configId, platform_object_id: platformObjectId, deal_name: '' },
            { timeout: 35000 }
        ));
        return this.mapHubSpotExportResponse(response.data);
    }

    async createAndLinkHubSpot(
        fileId: string,
        summary: SummaryRef,
        configId: string,
        dealName: string
    ): Promise<ExportPushResult> {
        const response = await this.withSummaryRef(summary, ref => this.api.post(
            `/export/${encodeURIComponent(fileId)}/${encodeURIComponent(ref)}/hubspot/create_and_link`,
            // Deze velden staan in de app zowel in het pad als in de body.
            { config_id: configId, file_id: fileId, meeting_type: ref, deal_name: dealName },
            { timeout: 35000 }
        ));
        return this.mapHubSpotExportResponse(response.data);
    }

    // Vertaalt de respons van een synchrone export-push (webhook/Praktijkdata)
    // naar één uniforme vorm voor de UI.
    private mapExportPushResponse(data: any): ExportPushResult {
        if (data?.success) {
            return {
                success: true,
                message: (data.display?.message ?? '').toString(),
                url: data.display?.url ? data.display.url.toString() : undefined,
            };
        }
        return {
            success: false,
            message: (data?.error ?? '').toString(),
        };
    }

    async pushToCustomWebhook(fileId: string, summary: SummaryRef, configId: string): Promise<ExportPushResult> {
        // De backend wacht synchroon (max. 30s) op het klantsysteem; de eigen
        // timeout moet daar ruim boven zitten zodat de backend eerder opgeeft
        // dan de client.
        const response = await this.withSummaryRef(summary, ref => this.api.post(
            `/export/${fileId}/${encodeURIComponent(ref)}/custom_webhook/push`,
            { config_id: configId },
            { timeout: 35000 }
        ));
        return this.mapExportPushResponse(response.data || {});
    }

    async searchPraktijkdata(
        fileId: string,
        summary: SummaryRef,
        configId: string,
        datumStart: string,
        datumEind: string
    ): Promise<PraktijkdataSearchResponse> {
        // Ook de zoekactie loopt synchroon door naar het EPD; zelfde ruime
        // timeout als de push-calls. Let op: resultaten bevatten cliëntnamen,
        // dus nooit (mee)loggen.
        const response = await this.withSummaryRef(summary, ref => this.api.post(
            `/export/${fileId}/${encodeURIComponent(ref)}/praktijkdata/search`,
            {
                config_id: configId,
                datum_start: datumStart,
                datum_eind: datumEind,
            },
            { timeout: 35000 }
        ));
        const data = response.data || {};
        const rawResults = Array.isArray(data.results) ? data.results : [];
        const results: PraktijkdataSearchResult[] = rawResults
            .filter((item: any) => item && item.id !== undefined && item.id !== null)
            .map((item: any) => ({
                id: item.id.toString(),
                clientId: Number(item.client_id ?? 0),
                dossierId: Number(item.dossier_id ?? 0),
                name: (item.name ?? '').toString(),
                afspraakId: item.afspraak_id !== undefined && item.afspraak_id !== null
                    ? Number(item.afspraak_id)
                    : undefined,
                datumTijd: item.datum_tijd ? item.datum_tijd.toString() : undefined,
                afspraaktype: item.afspraaktype ? item.afspraaktype.toString() : undefined,
                status: item.status ? item.status.toString() : undefined,
            }));
        return {
            results,
            canCreate: data.can_create === true,
            configId: (data.config_id ?? configId).toString(),
        };
    }

    async linkPraktijkdata(
        fileId: string,
        summary: SummaryRef,
        configId: string,
        platformObjectId: string
    ): Promise<ExportPushResult> {
        // Synchroon; backend wacht max. 30s op Praktijkdata → client-timeout ≥ 35s.
        const response = await this.withSummaryRef(summary, ref => this.api.post(
            `/export/${fileId}/${encodeURIComponent(ref)}/praktijkdata/link`,
            {
                config_id: configId,
                platform_object_id: platformObjectId,
            },
            { timeout: 35000 }
        ));
        return this.mapExportPushResponse(response.data || {});
    }

    async searchEduV(
        fileId: string,
        summary: SummaryRef,
        configId: string,
        searchTerm: string
    ): Promise<EduVSearchResponse> {
        // Het LAS kent geen zoekparameter op naam: de backend haalt de
        // leerlingenlijst van de school op en filtert die. Dus synchroon en
        // traag, en de resultaten bevatten leerlingnamen én ECK iD's —
        // nooit (mee)loggen.
        const response = await this.withSummaryRef(summary, ref => this.api.post(
            `/export/${fileId}/${encodeURIComponent(ref)}/edu_v/search`,
            {
                config_id: configId,
                search_term: searchTerm,
            },
            { timeout: 35000 }
        ));
        const data = response.data || {};
        const rawResults = Array.isArray(data.results) ? data.results : [];
        const results: EduVSearchResult[] = rawResults
            .filter((item: any) => item && item.id !== undefined && item.id !== null)
            .map((item: any) => ({
                id: item.id.toString(),
                name: (item.name ?? '').toString(),
                alias: item.alias ? item.alias.toString() : undefined,
            }));
        return {
            results,
            truncated: data.truncated === true,
            canCreate: data.can_create === true,
            configId: (data.config_id ?? configId).toString(),
        };
    }

    async linkEduV(
        fileId: string,
        summary: SummaryRef,
        configId: string,
        platformObjectId: string
    ): Promise<ExportPushResult> {
        // Synchroon; backend wacht max. 30s op het LAS → client-timeout ≥ 35s.
        const response = await this.withSummaryRef(summary, ref => this.api.post(
            `/export/${fileId}/${encodeURIComponent(ref)}/edu_v/link`,
            {
                config_id: configId,
                platform_object_id: platformObjectId,
            },
            { timeout: 35000 }
        ));
        return this.mapExportPushResponse(response.data || {});
    }

    async searchMijnKantoor(
        fileId: string,
        summary: SummaryRef,
        searchTerm: string,
        configId?: string
    ): Promise<MijnKantoorSearchResponse> {
        // Synchroon door naar Mijn Kantoor; zelfde ruime timeout als de andere
        // export-calls. Resultaten bevatten klantnamen — nooit (mee)loggen.
        // config_id alleen meesturen als de gebruiker expliciet een kantoor
        // gekozen heeft; bij één configuratie lost de backend hem zelf op.
        const payload: Record<string, string> = { search_term: searchTerm };
        if (configId) {
            payload.config_id = configId;
        }
        const response = await this.withSummaryRef(summary, ref => this.api.post(
            `/export/${fileId}/${encodeURIComponent(ref)}/mijnkantoor/search`,
            payload,
            { timeout: 35000 }
        ));
        const data = response.data || {};
        const rawResults = Array.isArray(data.results) ? data.results : [];
        const results: MijnKantoorSearchResult[] = rawResults
            .filter((item: any) => item && item.id !== undefined && item.id !== null)
            .map((item: any) => ({
                id: item.id.toString(),
                name: (item.name ?? '').toString(),
                number: item.number !== undefined && item.number !== null
                    ? item.number.toString()
                    : undefined,
                type: item.type ? item.type.toString() : undefined,
                city: item.city ? item.city.toString() : undefined,
                webUrl: item.web_url ? item.web_url.toString() : undefined,
            }));
        return {
            results,
            truncated: data.truncated === true,
            canCreate: data.can_create === true,
            configId: (data.config_id ?? configId ?? '').toString(),
        };
    }

    async linkMijnKantoor(
        fileId: string,
        summary: SummaryRef,
        configId: string,
        platformObjectId: string
    ): Promise<ExportPushResult> {
        // Synchroon; backend wacht max. 30s op Mijn Kantoor → client-timeout ≥ 35s.
        const response = await this.withSummaryRef(summary, ref => this.api.post(
            `/export/${fileId}/${encodeURIComponent(ref)}/mijnkantoor/link`,
            {
                config_id: configId,
                platform_object_id: platformObjectId,
            },
            { timeout: 35000 }
        ));
        return this.mapExportPushResponse(response.data || {});
    }

    async searchAfas(
        fileId: string,
        summary: SummaryRef,
        searchTerm: string,
        configId?: string
    ): Promise<AfasSearchResponse> {
        // Synchroon door naar AFAS Profit; zelfde ruime timeout als de andere
        // export-calls. Resultaten bevatten relatienamen — nooit (mee)loggen.
        // config_id alleen meesturen als de gebruiker expliciet een
        // configuratie gekozen heeft; bij één lost de backend hem zelf op.
        const payload: Record<string, string> = { search_term: searchTerm };
        if (configId) {
            payload.config_id = configId;
        }
        const response = await this.withSummaryRef(summary, ref => this.api.post(
            `/export/${fileId}/${encodeURIComponent(ref)}/afas/search`,
            payload,
            { timeout: 35000 }
        ));
        const data = response.data || {};
        const rawResults = Array.isArray(data.results) ? data.results : [];
        const results: AfasSearchResult[] = rawResults
            .filter((item: any) => item && item.id !== undefined && item.id !== null)
            .map((item: any) => {
                // details is een vrije key/value-bak met veldnamen uit de
                // AFAS-omgeving; alleen de waarden naar string dwingen, de
                // keys ongemoeid laten.
                let details: Record<string, string> | undefined;
                if (item.details && typeof item.details === 'object' && !Array.isArray(item.details)) {
                    const entries = Object.entries(item.details)
                        .filter(([, value]) => value !== undefined && value !== null && value !== '')
                        .map(([key, value]): [string, string] => [key, String(value)]);
                    if (entries.length > 0) {
                        details = Object.fromEntries(entries);
                    }
                }
                return {
                    id: item.id.toString(),
                    name: (item.name ?? '').toString(),
                    details,
                };
            });
        return {
            results,
            truncated: data.truncated === true,
            canCreate: data.can_create === true,
            configId: (data.config_id ?? configId ?? '').toString(),
        };
    }

    async linkAfas(
        fileId: string,
        summary: SummaryRef,
        configId: string,
        platformObjectId: string
    ): Promise<ExportPushResult> {
        // Synchroon; backend wacht max. 30s op AFAS → client-timeout ≥ 35s.
        const response = await this.withSummaryRef(summary, ref => this.api.post(
            `/export/${fileId}/${encodeURIComponent(ref)}/afas/link`,
            {
                config_id: configId,
                platform_object_id: platformObjectId,
            },
            { timeout: 35000 }
        ));
        return this.mapExportPushResponse(response.data || {});
    }

    async searchZgw(
        fileId: string,
        summary: SummaryRef,
        identificatie: string,
        configId?: string
    ): Promise<ZgwSearchResponse> {
        // Synchroon door naar het gemeentelijke zaaksysteem; zelfde ruime
        // timeout als de andere export-calls. Zaakomschrijvingen kunnen
        // persoonsgegevens bevatten — nooit (mee)loggen.
        // config_id alleen meesturen als de gebruiker expliciet een
        // configuratie gekozen heeft; bij één lost de backend hem zelf op.
        const payload: Record<string, string> = { identificatie };
        if (configId) {
            payload.config_id = configId;
        }
        const response = await this.withSummaryRef(summary, ref => this.api.post(
            `/export/${fileId}/${encodeURIComponent(ref)}/zgw/search`,
            payload,
            { timeout: 35000 }
        ));
        const data = response.data || {};
        const rawResults = Array.isArray(data.results) ? data.results : [];
        const results: ZgwSearchResult[] = rawResults
            .filter((item: any) => item && item.id !== undefined && item.id !== null)
            .map((item: any) => {
                const rawTypes = Array.isArray(item.informatieobjecttypen)
                    ? item.informatieobjecttypen
                    : [];
                const informatieobjecttypen: ZgwInformatieobjecttype[] = rawTypes
                    .filter((iot: any) => iot && iot.uuid)
                    .map((iot: any) => ({
                        uuid: iot.uuid.toString(),
                        omschrijving: (iot.omschrijving ?? '').toString(),
                        vertrouwelijkheidaanduiding: iot.vertrouwelijkheidaanduiding
                            ? iot.vertrouwelijkheidaanduiding.toString()
                            : undefined,
                        voorgesteld: iot.voorgesteld === true,
                    }));
                return {
                    id: item.id.toString(),
                    identificatie: (item.identificatie ?? '').toString(),
                    omschrijving: item.omschrijving ? item.omschrijving.toString() : undefined,
                    startdatum: item.startdatum ? item.startdatum.toString() : undefined,
                    zaaktypeOmschrijving: item.zaaktype_omschrijving
                        ? item.zaaktype_omschrijving.toString()
                        : undefined,
                    vertrouwelijkheidaanduiding: item.vertrouwelijkheidaanduiding
                        ? item.vertrouwelijkheidaanduiding.toString()
                        : undefined,
                    informatieobjecttypen,
                };
            });
        return {
            results,
            canCreate: data.can_create === true,
            configId: (data.config_id ?? configId ?? '').toString(),
            vertrouwelijkheidaanduidingen: Array.isArray(data.vertrouwelijkheidaanduidingen)
                ? data.vertrouwelijkheidaanduidingen.map((value: any) => value.toString())
                : [],
            vertrouwelijkheidaanduidingDefault: data.vertrouwelijkheidaanduiding_default
                ? data.vertrouwelijkheidaanduiding_default.toString()
                : undefined,
        };
    }

    async linkZgw(
        fileId: string,
        summary: SummaryRef,
        configId: string,
        platformObjectId: string
    ): Promise<ExportPushResult> {
        // Synchroon; backend wacht max. 30s op het zaaksysteem → client-timeout ≥ 35s.
        const response = await this.withSummaryRef(summary, ref => this.api.post(
            `/export/${fileId}/${encodeURIComponent(ref)}/zgw/link`,
            {
                config_id: configId,
                platform_object_id: platformObjectId,
            },
            { timeout: 35000 }
        ));
        return this.mapExportPushResponse(response.data || {});
    }

    async searchMFiles(
        fileId: string,
        summary: SummaryRef,
        searchTerm: string,
        configId?: string
    ): Promise<MFilesSearchResponse> {
        // Synchroon door naar de vault van de klant; zelfde ruime timeout als
        // de andere export-calls. Resultaten bevatten klantnamen uit de vault
        // — nooit (mee)loggen en nergens cachen.
        // config_id alleen meesturen als de gebruiker expliciet een
        // configuratie gekozen heeft; bij één lost de backend hem zelf op.
        const payload: Record<string, string> = { search_term: searchTerm };
        if (configId) {
            payload.config_id = configId;
        }
        const response = await this.withSummaryRef(summary, ref => this.api.post(
            `/export/${fileId}/${encodeURIComponent(ref)}/m_files/search`,
            payload,
            { timeout: 35000 }
        ));
        const data = response.data || {};
        const rawResults = Array.isArray(data.results) ? data.results : [];
        const results: MFilesSearchResult[] = rawResults
            .filter((item: any) => item && item.id !== undefined && item.id !== null)
            .map((item: any) => ({
                // Het samengestelde id ("101.123") letterlijk overnemen:
                // niet parsen, niet opnieuw samenstellen.
                id: item.id.toString(),
                name: (item.name ?? '').toString(),
                displayId: item.display_id !== undefined && item.display_id !== null && item.display_id !== ''
                    ? item.display_id.toString()
                    : undefined,
                lastModified: item.last_modified ? item.last_modified.toString() : undefined,
            }));
        return {
            results,
            moreResults: data.more_results === true,
            canCreate: data.can_create === true,
            configId: (data.config_id ?? configId ?? '').toString(),
        };
    }

    async linkMFiles(
        fileId: string,
        summary: SummaryRef,
        configId: string,
        platformObjectId: string
    ): Promise<ExportPushResult> {
        // Synchroon; het genereren van het document plus twee vault-calls kan
        // enkele seconden duren → client-timeout ruim boven de backend-timeout.
        const response = await this.withSummaryRef(summary, ref => this.api.post(
            `/export/${fileId}/${encodeURIComponent(ref)}/m_files/link`,
            {
                config_id: configId,
                platform_object_id: platformObjectId,
            },
            { timeout: 35000 }
        ));
        return this.mapExportPushResponse(response.data || {});
    }

    async pushToMFiles(fileId: string, summary: SummaryRef, configId: string): Promise<ExportPushResult> {
        // Alleen voor configuraties met export_method DOCUMENT; op een
        // DOCUMENT_LINKED-configuratie antwoordt de backend met een 400.
        const response = await this.withSummaryRef(summary, ref => this.api.post(
            `/export/${fileId}/${encodeURIComponent(ref)}/m_files/push`,
            { config_id: configId },
            { timeout: 35000 }
        ));
        return this.mapExportPushResponse(response.data || {});
    }

    async getExportUserSettings(configId: string): Promise<ExportUserSettings> {
        const response = await this.api.get(`/export/configurations/${configId}/user-settings`);
        const data = response.data || {};
        return {
            configId: (data.config_id ?? configId).toString(),
            targetPlatform: (data.target_platform ?? '').toString(),
            overridableFields: Array.isArray(data.overridable_fields)
                ? data.overridable_fields.map((field: any) => field.toString())
                : [],
            settings: data.settings && typeof data.settings === 'object' ? data.settings : {},
        };
    }

    async updateExportUserSettings(
        configId: string,
        settings: Record<string, number | string | null>
    ): Promise<void> {
        // null als waarde wist de override; alleen velden uit overridable_fields
        // zijn toegestaan (anders 400).
        await this.api.put(`/export/configurations/${configId}/user-settings`, { settings });
    }

    async getPraktijkdataMedewerkers(configId: string): Promise<PraktijkdataMedewerker[]> {
        // Beperkte proxy met gebruikers-JWT die alléén de medewerkers-lijst
        // teruggeeft; loopt door naar het EPD, dus zelfde ruime timeout.
        const response = await this.api.post(
            `/export/configurations/${configId}/referentiedata`,
            {},
            { timeout: 35000 }
        );
        const rawResults = Array.isArray(response.data?.results) ? response.data.results : [];
        return rawResults
            .filter((item: any) => item && (item.id ?? item.medewerker_id) !== undefined)
            .map((item: any) => {
                const id = Number(item.id ?? item.medewerker_id);
                const name = (
                    item.name ??
                    item.naam ??
                    [item.voorletters, item.tussenvoegsel, item.achternaam].filter(Boolean).join(' ')
                )?.toString().trim();
                return { id, name: name || `#${id}` };
            });
    }

    async getEduVEmployees(configId: string): Promise<EduVEmployee[]> {
        // Zelfde beperkte proxy als bij Praktijkdata, maar met de
        // employees-resource. Loopt door naar het LAS → ruime timeout.
        const response = await this.api.post(
            `/export/configurations/${configId}/referentiedata`,
            {},
            { timeout: 35000 }
        );
        const rawResults = Array.isArray(response.data?.results) ? response.data.results : [];
        return rawResults
            .map((item: any): EduVEmployee | null => {
                if (!item || typeof item !== 'object') return null;
                const id = item.id ?? item.employee_user_id ?? item.user_id ?? item.employeeId;
                if (id === undefined || id === null || id.toString() === '') return null;
                const name = (
                    item.name ??
                    item.naam ??
                    [
                        item.given_name ?? item.voornaam ?? item.roepnaam,
                        item.tussenvoegsel,
                        item.family_name ?? item.achternaam,
                    ]
                        .filter(Boolean)
                        .join(' ')
                )
                    ?.toString()
                    .trim();
                // Edu-V kent voor medewerkers geen primaire ketenidentifier, dus
                // naamgenoten zijn alleen te onderscheiden aan deze extra velden.
                const details = [
                    item.initials ?? item.initialen ?? item.afkorting,
                    item.email ?? item.emailadres,
                    item.role ?? item.rol ?? item.function ?? item.functie,
                ]
                    .filter(Boolean)
                    .map((value: any) => value.toString().trim())
                    .filter((value: string) => value !== '')
                    .join(' · ');
                const idType = item.id_type ?? item.idType ?? item.employee_user_id_type;
                return {
                    id: id.toString(),
                    idType: idType ? idType.toString() : undefined,
                    // Nooit terugvallen op het id: dat kan een eckId zijn.
                    name: name || '',
                    details: details || undefined,
                };
            })
            .filter((item: EduVEmployee | null): item is EduVEmployee => item !== null);
    }

    // File sharing
    async getFileShares(fileId: string): Promise<FileShare[]> {
        try {
            const response = await this.api.get(`/share/file/${fileId}`);
            const shares = response.data?.shares || response.data || [];
            if (Array.isArray(shares)) {
                return shares.map(share => this.mapFileShare(share));
            }
            if (shares && typeof shares === 'object') {
                return Object.values(shares).map(share => this.mapFileShare(share));
            }
            return [];
        } catch (error) {
            logger.error('Error fetching file shares:', error);
            throw error;
        }
    }

    async createFileShare(fileId: string, input: CreateFileShareInput): Promise<FileShare> {
        try {
            const payload: Record<string, unknown> = {
                target_type: input.targetType,
                permission: input.permission || 'view',
            };

            if (input.targetType === 'user') {
                payload.target_user_uuid = input.targetUserUuid;
            } else if (input.targetType === 'group') {
                payload.target_group_id = input.targetGroupId;
            }

            if (input.expiresAt) {
                payload.expires_at = input.expiresAt;
            }

            if (input.note !== undefined) {
                payload.note = input.note;
            }

            const response = await this.api.post(`/share/file/${fileId}`, payload);
            const share = response.data?.share || response.data;
            return this.mapFileShare(share);
        } catch (error) {
            logger.error('Error creating file share:', error);
            throw error;
        }
    }

    async revokeFileShare(fileId: string, shareId: number): Promise<FileShare> {
        try {
            const response = await this.api.delete(`/share/file/${fileId}/${shareId}`);
            const share = response.data?.share || response.data;
            return this.mapFileShare(share);
        } catch (error) {
            logger.error('Error revoking file share:', error);
            throw error;
        }
    }

    async getTeamGroupUsers(): Promise<TeamGroupUsersResponse> {
        try {
            const response = await this.api.get('/team/group/users');
            const group = response.data?.group || {};
            const members = Array.isArray(response.data?.members) ? response.data.members.map((member: any) => ({
                userUuid: member.user_uuid || member.userUuid || member.uuid,
                username: member.username || member.name || member.email || 'Onbekende gebruiker',
                email: member.email,
            })) : [];

            return {
                group: {
                    id: group.id ?? 0,
                    name: group.name ?? 'Onbekende groep',
                },
                members,
            };
        } catch (error) {
            logger.error('Error fetching team group users:', error);
            throw error;
        }
    }

    // Chat/AI interactions
    async sendChatMessage(fileId: string, summary: SummaryRef, message: string): Promise<string> {
        try {
            const response = await this.api.post('/chat', {
                file_id: fileId,
                meeting_type: getSummaryRef(summary),
                message
            }, {
                timeout: 60000
            });
            const { data } = response;

            if (typeof data === 'string') {
                return data;
            }

            if (data && typeof data === 'object') {
                if (typeof data.response === 'string') {
                    return data.response;
                }
                if (typeof data.message === 'string') {
                    return data.message;
                }
            }

            logger.warn('Chat endpoint returned unexpected payload format');
            return '';
        } catch (error) {
            logger.error('Error sending chat message:', error);
            throw error;
        }
    }

    // Streaming updates
    async *streamSummaryUpdates(fileId: string, meetingType: string, prompt: string): AsyncGenerator<string, void, unknown> {
        try {
            const response = await fetch(`${this.api.defaults.baseURL}/stream-summary`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${await this.authService.getValidAccessToken()}`
                },
                body: JSON.stringify({
                    file_id: fileId,
                    meeting_type: meetingType,
                    prompt
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const reader = response.body?.getReader();
            const decoder = new TextDecoder();

            if (!reader) {
                throw new Error('No response body');
            }

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value);
                const lines = chunk.split('\n');

                for (const line of lines) {
                    if (line.trim()) {
                        try {
                            const data = JSON.parse(line);
                            if (data.content) {
                                yield data.content;
                            }
                        } catch (e) {
                            // Ignore non-JSON lines
                        }
                    }
                }
            }
        } catch (error) {
            logger.error('Error streaming summary updates:', error);
            throw error;
        }
    }

    // File Upload
    async uploadChunk(
        chunk: Blob,
        fileName: string,
        chunkIndex: number,
        totalChunks: number,
        fileIndex: number,
        totalFiles: number,
        fileId: string,
        transcriptionMethod: string = 'whisper',
        oneSignalId?: string,
        firstSegmentCreatedAt?: string,
        fileSizeBytes?: number,
        chunkSizeBytes?: number
    ): Promise<any> {
        try {
            const formData = new FormData();
            formData.append('chunk', chunk);
            formData.append('fileName', fileName);
            formData.append('chunkIndex', chunkIndex.toString());
            formData.append('totalChunks', totalChunks.toString());
            formData.append('fileIndex', fileIndex.toString());
            formData.append('totalFiles', totalFiles.toString());
            debugFileId('sending fileId', fileId);
            assertValidFileId(fileId, 'src/services/apiService.ts uploadChunk before FormData.append(\'fileId\')');
            formData.append('fileId', fileId);
            formData.append('transcriptionMethod', transcriptionMethod);

            if (oneSignalId) {
                formData.append('oneSignalId', oneSignalId);
            }

            if (firstSegmentCreatedAt) {
                formData.append('firstSegmentCreatedAt', firstSegmentCreatedAt);
            }

            if (typeof fileSizeBytes === 'number') {
                formData.append('fileSize', fileSizeBytes.toString());
            }

            if (typeof chunkSizeBytes === 'number') {
                formData.append('chunkSize', chunkSizeBytes.toString());
            }

            formData.append('chunkByteSize', chunk.size.toString());

            const response = await this.api.post('/upload-chunk', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                timeout: UPLOAD_REQUEST_TIMEOUT_MS
            });

            return response.data;
        } catch (error) {
            logger.error('Error uploading chunk:', error);
            throw error;
        }
    }

    // Supporting Documents
    async uploadSupportingDocument(
        fileId: string,
        file: File,
        description?: string
    ): Promise<SupportingDocument> {
        try {
            const formData = new FormData();
            formData.append('file', file);
            if (description) {
                formData.append('description', description);
            }

            const response = await this.api.post(`/supporting-documents/${fileId}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                timeout: UPLOAD_REQUEST_TIMEOUT_MS
            });

            return response.data;
        } catch (error) {
            logger.error('Error uploading supporting document:', error);
            throw error;
        }
    }

    async addSupportingDocumentContent(
        fileId: string,
        content: string,
        filename: string,
        description?: string
    ): Promise<SupportingDocument> {
        try {
            const response = await this.api.post(`/supporting-documents/${fileId}`, {
                content,
                filename,
                description
            }, {
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            return response.data;
        } catch (error) {
            logger.error('Error adding supporting document content:', error);
            throw error;
        }
    }

    async updateSupportingDocumentContent(docId: number, content: string): Promise<SupportingDocument> {
        try {
            const response = await this.api.patch(`/supporting-documents/document/${docId}/content`, {
                content
            });
            return response.data;
        } catch (error) {
            logger.error('Error updating supporting document content:', error);
            throw error;
        }
    }

    async getSupportingDocuments(fileId: string): Promise<SupportingDocument[]> {
        try {
            const response = await this.api.get(`/supporting-documents/${fileId}`);
            return response.data.supporting_documents || [];
        } catch (error) {
            logger.error('Error fetching supporting documents:', error);
            return [];
        }
    }

    async getSupportingDocumentContent(docId: number): Promise<SupportingDocument | null> {
        try {
            const response = await this.api.get(`/supporting-documents/document/${docId}`);
            return response.data;
        } catch (error) {
            logger.error('Error fetching supporting document content:', error);
            return null;
        }
    }

    async deleteSupportingDocument(docId: number): Promise<boolean> {
        try {
            const response = await this.api.delete(`/supporting-documents/document/${docId}`);
            return response.status === 200;
        } catch (error) {
            logger.error('Error deleting supporting document:', error);
            throw error;
        }
    }

    async updateSupportingDocument(docId: number, description: string): Promise<SupportingDocument> {
        try {
            const response = await this.api.patch(`/supporting-documents/document/${docId}`, {
                description: description.trim() || null
            });
            return response.data;
        } catch (error) {
            logger.error('Error updating supporting document:', error);
            throw error;
        }
    }

    // Tags
    async getTags(): Promise<Tag[]> {
        try {
            const response = await this.api.get('/api/tags');
            return response.data.tags || [];
        } catch (error) {
            logger.error('Error fetching tags:', error);
            return [];
        }
    }

    async createTag(name: string, description?: string, color?: string): Promise<Tag> {
        try {
            const response = await this.api.post('/api/tags', {
                name,
                description,
                color: color || '#3B82F6' // Default blue color
            });
            return response.data.tag;
        } catch (error) {
            logger.error('Error creating tag:', error);
            throw error;
        }
    }

    async updateTag(tagId: number, updates: Partial<Tag>): Promise<Tag> {
        try {
            const response = await this.api.put(`/api/tags/${tagId}`, updates);
            return response.data.tag;
        } catch (error) {
            logger.error('Error updating tag:', error);
            throw error;
        }
    }

    async deleteTag(tagId: number): Promise<boolean> {
        try {
            const response = await this.api.delete(`/api/tags/${tagId}`);
            return response.status === 200;
        } catch (error) {
            logger.error('Error deleting tag:', error);
            throw error;
        }
    }

    async getRecordingTags(fileId: string): Promise<Tag[]> {
        try {
            const response = await this.api.get(`/api/file-progress/${fileId}/tags`);
            return response.data.tags || [];
        } catch (error) {
            logger.error('Error fetching recording tags:', error);
            return [];
        }
    }

    async addTagsToRecording(fileId: string, tagIds: number[]): Promise<boolean> {
        try {
            const response = await this.api.post(`/api/file-progress/${fileId}/tags`, {
                tag_ids: tagIds
            });
            return response.data.success;
        } catch (error) {
            logger.error('Error adding tags to recording:', error);
            throw error;
        }
    }

    async removeTagFromRecording(fileId: string, tagId: number): Promise<boolean> {
        try {
            const response = await this.api.delete(`/api/file-progress/${fileId}/tags/${tagId}`);
            return response.data.success;
        } catch (error) {
            logger.error('Error removing tag from recording:', error);
            throw error;
        }
    }

    async searchRecordingsByTags(tagIds?: number[], tagNames?: string[], matchAll: boolean = false): Promise<Recording[]> {
        try {
            const params = new URLSearchParams();

            if (tagIds?.length) {
                tagIds.forEach(id => params.append('tag_ids[]', id.toString()));
            }

            if (tagNames?.length) {
                tagNames.forEach(name => params.append('tag_names[]', name));
            }

            params.append('match_all', matchAll.toString());

            const response = await this.api.get(`/api/search/files-by-tags?${params.toString()}`);
            const files = response.data.files || [];

            // Map naar Recording format
            return files.map((file: any) => ({
                id: file.id.toString(),
                name: file.name,
                status: this.mapApiStatusToProcessingStatus(file.status),
                duration: this.formatDuration(file.audio_duration || 0),
                createdAt: file.created_at,
                updatedAt: file.updated_at || file.created_at,
                tags: file.tags || []
            }));
        } catch (error) {
            logger.error('Error searching recordings by tags:', error);
            return [];
        }
    }

    // Full-text zoeken in eigen opnames (titels, verslagen, transcripties, documenten, bestandsnamen en tags).
    // Server-default is 'relevance'; wij sturen bewust 'date_desc' mee (nieuwste eerst).
    async searchRecordings(q: string, limit: number = 20, sort: RecordingSearchSort = 'date_desc', signal?: AbortSignal): Promise<RecordingSearchResponse> {
        try {
            const response = await this.api.get(`/api/search?q=${encodeURIComponent(q)}&limit=${limit}&sort=${sort}`, { signal });
            const data = response.data || {};
            const rawResults = Array.isArray(data.results) ? data.results : [];

            return {
                status: data.status === 'building' ? 'building' : 'ready',
                totalCount: typeof data.total_count === 'number' ? data.total_count : rawResults.length,
                results: rawResults.map((item: any): RecordingSearchResult => {
                    const recordingDateTime = item.recording_datetime ?? item.recordingDatetime;
                    const matches: RecordingSearchMatch[] = (Array.isArray(item.matches) ? item.matches : []).map((match: any) => ({
                        kind: match.kind,
                        snippet: match.snippet?.toString() ?? '',
                        score: typeof match.score === 'number' ? match.score : null,
                        ...(match.meeting_type ? { meetingType: match.meeting_type } : {}),
                    }));

                    return {
                        recording: {
                            id: item.id.toString(),
                            name: item.name,
                            status: this.mapApiStatusToProcessingStatus(item.status),
                            duration: this.formatDuration(item.audio_duration || 0),
                            ...(recordingDateTime ? { recordingDateTime } : {}),
                            createdAt: item.created_at,
                            updatedAt: item.created_at,
                            tags: item.tags || [],
                        },
                        matches,
                    };
                }),
            };
        } catch (error) {
            // Geannuleerde requests (nieuwe zoekterm) zijn verwacht gedrag; niet loggen
            if ((error as any)?.code !== 'ERR_CANCELED' && (error as any)?.name !== 'CanceledError') {
                logger.error('Error searching recordings:', error);
            }
            throw error;
        }
    }

    // User Preferences
    async getUserPreferences(): Promise<UserPreferences> {
        try {
            const response = await this.api.get('/api/user/preferences');
            const data = response.data;
            return {
                personalContext: data.personal_context || data.personalContext || '',
                phoneNumber: data.phone_number || data.phoneNumber || '',
                authMethod: data.auth_method || data.authMethod || data.preferred_method || 'email',
                retentionDays: data.retention_days ?? null,
                organizationRetentionDays: data.organization_retention_days ?? null,
                effectiveRetentionDays: data.effective_retention_days ?? null,
                summaryRetentionDays: data.summary_retention_days ?? null,
                organizationSummaryRetentionDays: data.organization_summary_retention_days ?? null,
                effectiveSummaryRetentionDays: data.effective_summary_retention_days ?? null,
            };
        } catch (error) {
            logger.error('Error fetching user preferences:', error);
            // Return defaults if fetch fails
            return {
                personalContext: '',
                phoneNumber: '',
                authMethod: 'email',
                retentionDays: null,
                organizationRetentionDays: null,
                effectiveRetentionDays: null,
                summaryRetentionDays: null,
                organizationSummaryRetentionDays: null,
                effectiveSummaryRetentionDays: null,
            };
        }
    }

    async updateUserPreferences(preferences: Partial<UserPreferences>): Promise<UserPreferences> {
        try {
            const payload: Record<string, any> = {};

            if (preferences.personalContext !== undefined) {
                payload.personal_context = preferences.personalContext;
            }
            if (preferences.phoneNumber !== undefined) {
                payload.phone_number = preferences.phoneNumber;
            }
            if (preferences.authMethod !== undefined) {
                payload.auth_method = preferences.authMethod;
            }
            if (preferences.retentionDays !== undefined) {
                payload.retention_days = preferences.retentionDays;
            }
            // Alleen meesturen als het veld daadwerkelijk in het formulier stond:
            // een ontbrekende sleutel laat de opgeslagen waarde ongemoeid, null wist hem.
            if (preferences.summaryRetentionDays !== undefined) {
                payload.summary_retention_days = preferences.summaryRetentionDays;
            }

            const response = await this.api.post('/api/user/preferences', payload);
            if (response.status !== 200) {
                throw new Error('Failed to update user preferences');
            }
            const data = response.data;
            return {
                personalContext: data.personal_context || data.personalContext || '',
                phoneNumber: data.phone_number || data.phoneNumber || '',
                authMethod: data.auth_method || data.authMethod || data.preferred_method || 'email',
                retentionDays: data.retention_days ?? null,
                organizationRetentionDays: data.organization_retention_days ?? null,
                effectiveRetentionDays: data.effective_retention_days ?? null,
                summaryRetentionDays: data.summary_retention_days ?? null,
                organizationSummaryRetentionDays: data.organization_summary_retention_days ?? null,
                effectiveSummaryRetentionDays: data.effective_summary_retention_days ?? null,
            };
        } catch (error) {
            logger.error('Error updating user preferences:', error);
            throw error;
        }
    }

    // Template Suggestions

    async getTemplateSuggestions(params?: {
        status?: TemplateSuggestionStatus;
        template_id?: number;
        backfill?: boolean;
    }): Promise<TemplateSuggestion[]> {
        try {
            const queryParams = new URLSearchParams();
            if (params?.status) queryParams.append('status', params.status);
            if (params?.template_id) queryParams.append('template_id', params.template_id.toString());
            if (params?.backfill !== undefined) queryParams.append('backfill', params.backfill.toString());

            const query = queryParams.toString();
            const response = await this.api.get(`/template-suggestions${query ? `?${query}` : ''}`);
            return response.data?.suggestions || response.data || [];
        } catch (error) {
            logger.error('Error fetching template suggestions:', error);
            throw error;
        }
    }

    async getTemplateSuggestion(id: number): Promise<TemplateSuggestion> {
        try {
            const response = await this.api.get(`/template-suggestions/${id}`);
            return response.data?.suggestion || response.data;
        } catch (error) {
            logger.error('Error fetching template suggestion:', error);
            throw error;
        }
    }

    async updateTemplateSuggestion(id: number, data: {
        normalized_instruction?: string;
        target_components?: TemplateComponent[];
        resolution_note?: string;
    }): Promise<TemplateSuggestion> {
        try {
            const response = await this.api.patch(`/template-suggestions/${id}`, data);
            return response.data?.suggestion || response.data;
        } catch (error: any) {
            logger.error('Error updating template suggestion:', error);
            if (error?.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
            throw error instanceof Error ? error : new Error('Fout bij het bijwerken van suggestion');
        }
    }

    async acceptTemplateSuggestion(id: number, data?: {
        model?: string;
        normalized_instruction?: string;
        target_components?: TemplateComponent[];
        component_mode?: string;
        summary_preferences?: any;
        resolution_note?: string;
    }): Promise<any> {
        try {
            const response = await this.api.post(`/template-suggestions/${id}/accept`, data || {});
            return response.data;
        } catch (error: any) {
            logger.error('Error accepting template suggestion:', error);
            if (error?.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
            throw error instanceof Error ? error : new Error('Fout bij het accepteren van suggestion');
        }
    }

    async rejectTemplateSuggestion(id: number, data?: {
        reason?: string;
        resolution_note?: string;
    }): Promise<any> {
        try {
            const response = await this.api.post(`/template-suggestions/${id}/reject`, data || {});
            return response.data;
        } catch (error: any) {
            logger.error('Error rejecting template suggestion:', error);
            if (error?.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
            throw error instanceof Error ? error : new Error('Fout bij het afwijzen van suggestion');
        }
    }
}

export default ApiService; 
